import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ImagesGallery from './ImagesGallery';
import Documents from './Documents';
import PaymentModal from './PaymentModal';
import WaterSmartCard from './WaterSmartCard';
import { useServiceLocation } from './ServiceLocationContext';
import InspectionsCard from './Inspections';
import PropertyPicker from './PropertyPicker';
import { clearPortalSession } from './portalAuth';
import { addressLines } from './addressLines';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Shared zone-flow-in-gallons helpers (see backend/watersmart_zone_metrics.py).
// Rachio's own zone.flow ("nozzle inchesPerHour") is an application-rate
// unit, not gallons — every Flow display on this dashboard converts via the
// real metered GPM (from WaterSmart, via the zone-metrics endpoint) x the
// zone's scheduled runtime instead, matching the gallon-based rest of the
// water conservation feature (see the same fix in WaterConservation.js).
function zoneMetricsRowFor(zoneMetrics, zone) {
  const rows = zoneMetrics?.rows || [];
  const byNumber = zone.number != null ? rows.find((r) => r.zone_number === zone.number) : null;
  return byNumber || rows.find((r) => r.zone_name === zone.name) || null;
}
function parseRuntimeMinutes(runtime) {
  if (!runtime) return null;
  const m = /^(\d+(?:\.\d+)?)/.exec(runtime);
  return m ? parseFloat(m[1]) : null;
}
function zoneFlowGallonsLabel(zoneMetrics, zone) {
  const row = zoneMetricsRowFor(zoneMetrics, zone);
  const runtimeMin = parseRuntimeMinutes(zone.runtime);
  const gal = row?.current_gpm != null && runtimeMin != null
    ? Math.round(row.current_gpm * runtimeMin)
    : null;
  return gal != null ? `${gal} gal` : '—';
}

// Admin actions (schedule create/delete, zone toggle, etc.) require the staff
// admin key. It is intentionally NOT baked into this bundle — every visitor's
// browser can view-source a static build, so a hardcoded key here would ship
// a live master credential to the public. Staff are prompted for it once per
// session (via ?admin=1) and it's kept only in sessionStorage, never in code.
function getAdminKey() {
  let key = sessionStorage.getItem('admin_key');
  if (!key) {
    key = window.prompt('Enter admin key:') || '';
    if (key) sessionStorage.setItem('admin_key', key);
  }
  return key;
}

// ── Watering Schedule Card ───────────────────────────────────────────────────
function WateringScheduleCard({ schedule }) {
  if (!schedule) return null;

  // 2026-10-18: ERP's effective watering days for this property: the same calculation as the
  // Zone Schedule page's Watering Days box (city rule, verified Allen days, or staff "Manually Set").
  // The two older shapes below are kept so a backend rollback still renders.
  if ('verified' in schedule) {
    const manual = schedule.mode === 'manual';
    const season = schedule.season || {};
    const title = schedule.verified && !manual && schedule.city ? `City of ${schedule.city} — Watering Schedule` : 'Watering Schedule';
    return (
      <div className="watering-card card" data-testid="watering-card">
        <div className="watering-header">
          <span className="watering-icon">💧</span>
          <h3>{title}</h3>
          {manual ? (
            <span className="watering-season-badge manual" data-testid="wc-manual">Manually Set</span>
          ) : season.name && (
            <span className={`watering-season-badge ${season.key === 'summer' ? 'in-season' : 'off-season'}`}>{season.name}</span>
          )}
        </div>
        {schedule.verified ? (
          <div className="watering-body">
            <div className="watering-days">
              <span className="watering-days-label">Your designated days:</span>
              <strong className="watering-days-value" data-testid="wc-days">{schedule.days_display}</strong>
            </div>
            {schedule.rule && <p className="watering-rule">{schedule.rule}</p>}
            {schedule.note && <p className="watering-note watering-stale" data-testid="wc-note">{schedule.note}</p>}
            {!manual && season.dates && (
              <p className="watering-note">{season.name} schedule {season.dates}{season.frequency ? ` · ${season.frequency}` : ''}</p>
            )}
          </div>
        ) : (
          <>
            <p className="watering-text" data-testid="wc-not-verified">
              {schedule.message || 'Watering days not verified'}{schedule.city ? ` · ${schedule.city}` : ''}
            </p>
            {schedule.note && <p className="watering-note" data-testid="wc-note">{schedule.note}</p>}
          </>
        )}
        <p className="watering-footer">
          {!manual && schedule.verified && schedule.city === 'Plano' && (
            <>HOAs &amp; Common Areas follow the even-numbered schedule.<br /></>
          )}
          {schedule.source?.url ? (
            <a href={schedule.source.url} target="_blank" rel="noreferrer">{schedule.source.name} ↗</a>
          ) : schedule.source?.name}
          {schedule.source && <br />}
          For information only — this does not change your controller.
        </p>
      </div>
    );
  }

  if (schedule.source === 'manual') {
    return (
      <div className="watering-card card">
        <div className="watering-header">
          <span className="watering-icon">💧</span>
          <h3>Watering Schedule</h3>
        </div>
        <p className="watering-text">{schedule.text}</p>
      </div>
    );
  }

  if (schedule.source === 'auto_plano') {
    return (
      <div className="watering-card card">
        <div className="watering-header">
          <span className="watering-icon">💧</span>
          <h3>City of Plano — Watering Schedule</h3>
          <span className={`watering-season-badge ${schedule.in_season ? 'in-season' : 'off-season'}`}>
            {schedule.in_season ? 'Active Season' : 'Off Season'}
          </span>
        </div>
        <div className="watering-body">
          <div className="watering-days">
            <span className="watering-days-label">Your designated days:</span>
            <strong className="watering-days-value">{schedule.days}</strong>
          </div>
          <p className="watering-rule">
            {schedule.parity}-numbered address (ends in {schedule.last_digit})
          </p>
          {!schedule.in_season && (
            <p className="watering-note">Restrictions active {schedule.season}</p>
          )}
          {schedule.in_season && (
            <p className="watering-note">Restrictions in effect through October 31</p>
          )}
        </div>
        <p className="watering-footer">
          HOAs &amp; Common Areas follow the even-numbered schedule (Mon &amp; Thu).
          <br />
          <a href="https://www.plano.gov/2005/City-Watering-Schedule" target="_blank" rel="noreferrer">
            Plano watering rules ↗
          </a>
        </p>
      </div>
    );
  }

  return null;
}

// ── Weather Widget ─────────────────────────────────────────────────────────
// Uses Open-Meteo (free, no API key). Default coords = Allen TX (primary service city).
const WMO_ICONS = {
  0:'☀️',1:'🌤️',2:'⛅',3:'☁️',45:'🌫️',48:'🌫️',
  51:'🌦️',53:'🌦️',55:'🌧️',61:'🌧️',63:'🌧️',65:'🌧️',
  71:'❄️',73:'❄️',75:'❄️',80:'🌦️',81:'🌧️',82:'⛈️',
  95:'⛈️',96:'⛈️',99:'⛈️',
};
const WMO_DESC = {
  0:'Clear',1:'Mainly clear',2:'Partly cloudy',3:'Overcast',
  45:'Fog',48:'Icy fog',51:'Light drizzle',53:'Drizzle',55:'Heavy drizzle',
  61:'Light rain',63:'Rain',65:'Heavy rain',71:'Light snow',73:'Snow',75:'Heavy snow',
  80:'Showers',81:'Heavy showers',82:'Violent showers',95:'Thunderstorm',
  96:'Thunderstorm+hail',99:'Heavy thunderstorm',
};

function WeatherWidget({ lat = 33.1032, lon = -96.6706 }) {
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
      `&current=temperature_2m,weathercode,windspeed_10m,relative_humidity_2m` +
      `&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
      `&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FChicago&forecast_days=5`
    )
      .then(r => r.json())
      .then(d => setWeather(d))
      .catch(() => setError(true));
  }, [lat, lon]);

  if (error || !weather) return null;

  const cur = weather.current;
  const daily = weather.daily;
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-4">Local Weather · North TX</p>

      {/* Current conditions */}
      <div className="flex items-center gap-4 mb-5">
        <span className="text-5xl">{WMO_ICONS[cur.weathercode] || '🌡️'}</span>
        <div>
          <p className="text-4xl font-bold text-white">{Math.round(cur.temperature_2m)}°F</p>
          <p className="text-sm text-slate-300">{WMO_DESC[cur.weathercode] || 'Unknown'}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            Humidity {cur.relative_humidity_2m}% · Wind {Math.round(cur.windspeed_10m)} mph
          </p>
        </div>
      </div>

      {/* 5-day forecast */}
      <div className="grid grid-cols-5 gap-1.5">
        {daily.time.map((dateStr, i) => {
          const d = new Date(dateStr + 'T12:00:00');
          return (
            <div key={dateStr} className="rounded-[16px] border border-white/8 bg-white/5 p-2 text-center">
              <p className="text-xs font-semibold text-slate-400">{i === 0 ? 'Today' : days[d.getDay()]}</p>
              <p className="text-xl my-1">{WMO_ICONS[daily.weathercode[i]] || '🌡️'}</p>
              <p className="text-xs font-bold text-white">{Math.round(daily.temperature_2m_max[i])}°</p>
              <p className="text-xs text-slate-500">{Math.round(daily.temperature_2m_min[i])}°</p>
              {daily.precipitation_probability_max[i] > 20 && (
                <p className="text-xs text-cyan-300 mt-0.5">💧{daily.precipitation_probability_max[i]}%</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ControllerNotLinked({ onSkip, wateringSchedule }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#1186c5_0%,_#0b3b57_35%,_#082c40_100%)] text-white flex flex-col items-center justify-center gap-6 p-6">
      <div className="max-w-md w-full rounded-[28px] border border-white/10 bg-slate-950/60 p-8 shadow-2xl backdrop-blur-xl text-center">
        <div className="mb-6 flex justify-center">
          <img src="/logo.png" alt="AAA Irrigation Service" className="h-16 w-16 rounded-full border border-cyan-300/20" />
        </div>
        <h2 className="text-2xl font-semibold text-white mb-2">Controller Not Yet Linked</h2>
        <p className="text-sm text-slate-300 mb-6 leading-6">
          We manage your <strong className="text-cyan-200">Rachio</strong> controller directly through our service account.
          Your system will appear here once our team has linked your controller to your account.
        </p>
        <div className="rounded-[20px] border border-cyan-300/15 bg-cyan-400/10 p-4 text-sm text-cyan-100 text-left mb-6">
          <p className="font-semibold mb-1">What happens next:</p>
          <ul className="list-disc list-inside space-y-1 text-cyan-50/80">
            <li>Our technician verifies your Rachio controller</li>
            <li>We link it to your portal account</li>
            <li>Live zone data and schedules appear here automatically</li>
          </ul>
        </div>
        <a
          href="tel:4697513567"
          className="block w-full rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] py-3 text-sm font-bold tracking-wide text-white shadow-lg mb-3"
        >
          Call Us -- 469-751-3567
        </a>
        <button
          onClick={() => navigate('/water-conservation')}
          className="block w-full rounded-xl border border-cyan-300/20 bg-cyan-400/10 py-3 text-sm font-medium text-cyan-100 hover:bg-cyan-400/20 transition mb-3"
        >
          Water Conservation Portal
        </button>
        <button
          onClick={onSkip}
          className="block w-full rounded-xl border border-white/10 bg-white/5 py-3 text-sm font-medium text-slate-300 hover:bg-white/10 transition"
        >
          I don't have a Rachio -- go to my dashboard
        </button>
      </div>
      {/* Watering days belong to the property, not the controller: shown here too (2026-10-18). */}
      {wateringSchedule && (
        <div className="max-w-md w-full text-left" data-testid="not-linked-watering">
          <WateringScheduleCard schedule={wateringSchedule} />
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function fmtCurrency(val) {
  const n = parseFloat(val) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function StatusBadge({ status, approvalStatus, pipelineStatus }) {
  // custom_pipeline_status is the canonical, actively-maintained status ladder
  // (Draft -> ... -> Paid), computed server-side and kept in sync by payment/invoice
  // hooks. It takes priority whenever present — approvalStatus/status are legacy
  // fields that can go stale (e.g. custom_approval_status stuck on "Draft" for a
  // Quotation that was submitted+invoiced+paid through a path that bypassed
  // manual_approve_estimate/async_submit_quotation). Falling back to them keeps
  // old behavior for any doc that predates the pipeline-status rollout.
  const raw = pipelineStatus || approvalStatus || status || '';
  const s = raw.toLowerCase();
  const display = s === 'ordered' ? 'Complete' : raw;
  const styles =
    s === 'paid'              ? 'bg-emerald-400/20 text-emerald-300 border border-emerald-400/30' :
    s === 'approved'          ? 'bg-emerald-400/20 text-emerald-300 border border-emerald-400/30' :
    s === 'declined'          ? 'bg-rose-400/20 text-rose-300 border border-rose-400/30' :
    s === 'sent for approval' ? 'bg-orange-500/25 text-orange-200 border border-orange-400/40' :
    s === 'submitted'         ? 'bg-blue-400/15 text-blue-200' :
    s === 'open'              ? 'bg-blue-400/15 text-blue-200' :
    s === 'ordered'           ? 'bg-emerald-400/15 text-emerald-200' :
    s === 'overdue'           ? 'bg-rose-400/15 text-rose-200' :
    s === 'unpaid'            ? 'bg-amber-400/15 text-amber-200' :
    s === 'draft'             ? 'bg-slate-400/15 text-slate-300' :
    s === 'cancelled'         ? 'bg-slate-400/15 text-slate-500' :
    s === 'lost'              ? 'bg-slate-400/15 text-slate-400' :
                                'bg-white/10 text-slate-300';
  const icon =
    s === 'paid'              ? '✓ ' :
    s === 'approved'          ? '✓ ' :
    s === 'ordered'           ? '✓ ' :
    s === 'declined'          ? '✕ ' :
    s === 'sent for approval' ? '⏳ ' : '';
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${styles}`}>
      {icon}{display || 'Unknown'}
    </span>
  );
}

function ScheduleModal({ customer, onClose }) {
  const [form, setForm] = useState({
    service_type: '',
    preferred_date: '',
    preferred_time: 'Morning (8am-12pm)',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type || !form.preferred_date) {
      setError('Please fill in service type and preferred date.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/schedule-appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customer.name || customer.customer_name || undefined,
          email: customer.email,
          service: form.service_type,
          preferred_date: form.preferred_date,
          preferred_time: form.preferred_time,
          notes: form.description,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to submit request');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-[28px] border border-white/10 bg-[#0b1e30] shadow-2xl p-7">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Schedule a Service</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition text-2xl leading-none">&times;</button>
        </div>

        {success ? (
          <div className="text-center py-6">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-emerald-400/15 p-4">
                <svg className="w-10 h-10 text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Request Submitted</h3>
            <p className="text-sm text-slate-300 mb-6 leading-6">We received your service request. Our team will confirm your appointment within 1 business day.</p>
            <button onClick={onClose} className="rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] px-6 py-3 text-sm font-bold text-white">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Service Type</label>
              <select
                value={form.service_type}
                onChange={e => set('service_type', e.target.value)}
                required
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
              >
                <option value="" className="bg-[#0b1e30]">Select a service...</option>
                <option value="Spring Startup" className="bg-[#0b1e30]">Spring Startup</option>
                <option value="Winterization" className="bg-[#0b1e30]">Winterization</option>
                <option value="System Repair" className="bg-[#0b1e30]">System Repair</option>
                <option value="New Zone Install" className="bg-[#0b1e30]">New Zone Install</option>
                <option value="Controller Upgrade" className="bg-[#0b1e30]">Controller Upgrade</option>
                <option value="Full Inspection" className="bg-[#0b1e30]">Full Inspection</option>
                <option value="Other" className="bg-[#0b1e30]">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Preferred Date</label>
              <input
                type="date"
                value={form.preferred_date}
                onChange={e => set('preferred_date', e.target.value)}
                required
                min={new Date().toISOString().split('T')[0]}
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Preferred Time</label>
              <select
                value={form.preferred_time}
                onChange={e => set('preferred_time', e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
              >
                <option value="Morning (8am-12pm)" className="bg-[#0b1e30]">Morning (8am - 12pm)</option>
                <option value="Afternoon (12pm-5pm)" className="bg-[#0b1e30]">Afternoon (12pm - 5pm)</option>
                <option value="Any Time" className="bg-[#0b1e30]">Any Time</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Notes (optional)</label>
              <textarea
                value={form.description}
                onChange={e => set('description', e.target.value)}
                rows={3}
                placeholder="Describe the issue or anything we should know..."
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-cyan-400/50 focus:outline-none resize-none"
              />
            </div>
            {error && <p className="text-sm text-rose-300 bg-rose-400/10 rounded-xl px-4 py-3">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] py-3 text-sm font-bold tracking-wide text-white shadow-lg disabled:opacity-60 transition hover:translate-y-[-1px]"
            >
              {loading ? 'Submitting...' : 'Submit Service Request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function EstimateDetailModal({ customerId, quotationId, onClose, onActioned }) {
  const [doc, setDoc] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [busy, setBusy] = useState(''); // 'approving' | 'declining'
  const [result, setResult] = useState(null); // { success, message, action }

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}`)
      .then(r => r.json())
      .then(d => { if (d.data) setDoc(d.data); else setLoadErr('Could not load estimate.'); })
      .catch(() => setLoadErr('Could not load estimate.'));
  }, [customerId, quotationId]);

  const act = async (action) => {
    setBusy(action === 'approve' ? 'approving' : 'declining');
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}/${action}`,
        { method: 'POST' }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Request failed');
      setResult({ success: true, message: d.message, action });
      onActioned();
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setBusy('');
    }
  };

  const canAct = doc && doc.docstatus === 0 && ['Draft','Sent for Approval'].includes(doc.custom_approval_status);
  const items = doc?.items || [];
  const taxes = doc?.taxes || [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 bg-black/70 backdrop-blur-sm overflow-y-auto">
      <div className="w-full max-w-2xl rounded-[28px] border border-white/10 bg-[#0b1e30] shadow-2xl p-7 mb-10">
        {/* Modal header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">Estimate</p>
            <h2 className="text-xl font-semibold text-white mt-1">{quotationId}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition text-2xl leading-none">&times;</button>
        </div>

        {/* Loading / error */}
        {!doc && !loadErr && (
          <div className="flex items-center gap-3 text-sm text-slate-400 py-8 justify-center">
            <div className="w-5 h-5 border border-slate-500 border-t-cyan-400 rounded-full animate-spin" />
            Loading estimate...
          </div>
        )}
        {loadErr && <p className="text-rose-300 text-sm py-4">{loadErr}</p>}

        {doc && (
          <>
            {/* Meta row */}
            <div className="flex flex-wrap gap-4 mb-6 text-sm">
              <div className="rounded-xl border border-white/8 bg-white/5 px-4 py-2">
                <p className="text-xs text-slate-400">Date</p>
                <p className="text-white font-medium">{fmtDate(doc.transaction_date)}</p>
              </div>
              <div className="rounded-xl border border-white/8 bg-white/5 px-4 py-2">
                <p className="text-xs text-slate-400">Status</p>
                <div className="mt-0.5"><StatusBadge status={doc.status} /></div>
              </div>
              {doc.custom_scheduled_date && (
                <div className="rounded-xl border border-white/8 bg-white/5 px-4 py-2">
                  <p className="text-xs text-slate-400">Scheduled Date</p>
                  <p className="text-white font-medium">{fmtDate(doc.custom_scheduled_date)}</p>
                </div>
              )}
            </div>

            {/* Service description */}
            {doc.custom_service_description && (
              <div className="rounded-[18px] border border-cyan-300/10 bg-cyan-400/5 px-5 py-4 mb-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-cyan-200/70 mb-2">Scope of Work</p>
                <p className="text-sm text-slate-200 leading-6 whitespace-pre-line">{doc.custom_service_description}</p>
              </div>
            )}

            {/* Line items table */}
            {items.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-3">Items</p>
                <div className="overflow-hidden rounded-[18px] border border-white/8">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-white/5 text-slate-400">
                      <tr>
                        <th className="px-4 py-3 font-medium">Zone</th>
                        <th className="px-4 py-3 font-medium">Description</th>
                        <th className="px-4 py-3 font-medium text-right">Qty</th>
                        <th className="px-4 py-3 font-medium text-right">Rate</th>
                        <th className="px-4 py-3 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => (
                        <tr key={i} className="border-t border-white/8 bg-slate-900/20">
                          <td className="px-4 py-3 text-slate-300 text-sm">{item.custom_zone || item.zone || ''}</td>
                          <td className="px-4 py-3 text-white">
                            <p className="font-medium">{item.item_name || item.item_code}</p>
                            {item.description && item.description !== item.item_name && (
                              <p className="text-xs text-slate-400 mt-0.5 leading-4">{(item.description || '').replace(/<[^>]+>/g, '').trim()}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-200 text-right">{item.qty}</td>
                          <td className="px-4 py-3 text-slate-200 text-right">{fmtCurrency(item.rate)}</td>
                          <td className="px-4 py-3 text-white font-semibold text-right">{fmtCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="rounded-[18px] border border-white/8 bg-white/5 px-5 py-4 mb-6 space-y-2 text-sm">
              {doc.total_taxes_and_charges > 0 && (
                <>
                  <div className="flex justify-between text-slate-300">
                    <span>Subtotal</span>
                    <span>{fmtCurrency(doc.net_total)}</span>
                  </div>
                  {taxes.map((t, i) => (
                    <div key={i} className="flex justify-between text-slate-300">
                      <span>{t.description || t.charge_type}</span>
                      <span>{fmtCurrency(t.tax_amount)}</span>
                    </div>
                  ))}
                </>
              )}
              <div className="flex justify-between text-white font-bold text-base pt-1 border-t border-white/10">
                <span>Total</span>
                <span>{fmtCurrency(doc.grand_total)}</span>
              </div>
            </div>

            {/* Result message */}
            {result && (
              <div className={`rounded-xl px-4 py-3 text-sm mb-4 ${result.success ? 'bg-emerald-400/10 text-emerald-300' : 'bg-rose-400/10 text-rose-300'}`}>
                {result.message}
              </div>
            )}

            {/* Action buttons */}
            {canAct && !result?.success && (
              <div className="flex gap-3">
                <button
                  onClick={() => act('approve')}
                  disabled={!!busy}
                  className="flex-1 rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] py-3 text-sm font-bold text-white shadow-lg disabled:opacity-50 transition hover:translate-y-[-1px]"
                >
                  {busy === 'approving' ? 'Approving...' : 'Approve Estimate'}
                </button>
                <button
                  onClick={() => act('decline')}
                  disabled={!!busy}
                  className="flex-1 rounded-xl border border-rose-400/30 bg-rose-400/10 py-3 text-sm font-semibold text-rose-300 disabled:opacity-50 transition hover:bg-rose-400/20"
                >
                  {busy === 'declining' ? 'Declining...' : 'Decline'}
                </button>
              </div>
            )}
            {result?.success && (
              <button onClick={onClose} className="w-full rounded-xl bg-white/10 border border-white/10 py-3 text-sm font-medium text-white">
                Close
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EstimatesSection({ customerId }) {
  const { selected } = useServiceLocation();
  const [data, setData] = useState(null);

  const load = () => {
    if (!customerId) return;
    const url = `${BACKEND_URL}/api/customer/${customerId}/quotations` +
      (selected ? `?service_location=${encodeURIComponent(selected)}` : '');
    fetch(url)
      .then(r => r.json())
      .then(d => setData(d.data || []))
      .catch(() => setData([]));
  };

  useEffect(() => { load(); }, [customerId, selected]);

  const estimates = data || [];
  const canAct = (q) => q.docstatus === 0 && ['Draft','Pending Approval','Sent for Approval'].includes(q.custom_approval_status);
  const openEstimate = (name) => window.open(`/estimates?id=${name}&customer=${encodeURIComponent(customerId)}`, '_blank');

  return (
    <>
      <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-5">Estimates</p>
        {data === null ? (
          <div className="flex items-center gap-3 text-sm text-slate-400 py-2">
            <div className="w-4 h-4 border border-slate-500 border-t-cyan-400 rounded-full animate-spin" />
            Loading estimates...
          </div>
        ) : estimates.length === 0 ? (
          <p className="text-sm text-slate-400">No estimates on file yet.</p>
        ) : (
          <div className="space-y-3">
            {estimates.map(q => (
              <div key={q.name} className="rounded-[20px] border border-white/8 bg-white/5 p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-white">{q.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDate(q.transaction_date)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-white">{fmtCurrency(q.grand_total)}</span>
                    <StatusBadge status={q.status} approvalStatus={q.custom_approval_status} pipelineStatus={q.custom_pipeline_status} />
                  </div>
                </div>
                {q.custom_service_description && (
                  <p className="mt-2 text-xs text-slate-400 leading-5 line-clamp-2">{q.custom_service_description}</p>
                )}
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => openEstimate(q.name)}
                    className={`flex-1 rounded-xl py-2.5 text-sm font-bold text-white text-center shadow-lg transition hover:translate-y-[-1px] ${canAct(q) ? 'bg-[linear-gradient(90deg,#ea580c,#f97316)]' : 'bg-cyan-400/10 border border-cyan-300/20 text-cyan-100 font-semibold'}`}
                  >
                    {canAct(q) ? '✍ View & Sign Estimate' : '📄 View Estimate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function LettersSection({ customerId }) {
  const [data, setData] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    fetch(`${BACKEND_URL}/api/customer/${customerId}/letters`)
      .then(r => r.json())
      .then(d => setData(d.data || []))
      .catch(() => setData([]));
  }, [customerId]);

  const letters = data || [];
  const viewLetter = (name) => {
    window.location.href = `/letter/${encodeURIComponent(customerId)}/${encodeURIComponent(name)}`;
  };
  const downloadPdf = async (name) => {
    setDownloadingId(name);
    try {
      const res = await fetch(`${BACKEND_URL}/api/customer/${customerId}/letter/${name}/pdf`);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // best-effort — no toast infra in this component
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-5">Letters</p>
      {data === null ? (
        <div className="flex items-center gap-3 text-sm text-slate-400 py-2">
          <div className="w-4 h-4 border border-slate-500 border-t-cyan-400 rounded-full animate-spin" />
          Loading letters...
        </div>
      ) : letters.length === 0 ? (
        <p className="text-sm text-slate-400">No letters on file yet.</p>
      ) : (
        <div className="space-y-3">
          {letters.map(letter => (
            <div key={letter.name} className="rounded-[20px] border border-white/8 bg-white/5 p-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-sm font-semibold text-white">{letter.title}</p>
                  {letter.letter_date && (
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDate(letter.letter_date)}</p>
                  )}
                </div>
                {letter.recipient_name && (
                  <span className="text-xs text-slate-400">{letter.recipient_name}</span>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => viewLetter(letter.name)}
                  className="flex-1 py-2 rounded-xl text-white text-xs font-semibold hover:opacity-90 transition"
                  style={{ background: '#1b7abf' }}
                >
                  📄 View Letter
                </button>
                <button
                  onClick={() => downloadPdf(letter.name)}
                  disabled={downloadingId === letter.name}
                  className="px-4 py-2 rounded-xl text-white text-xs font-semibold hover:opacity-90 transition disabled:opacity-50"
                  style={{ background: '#334155' }}
                >
                  {downloadingId === letter.name ? '⏳' : '⬇ PDF'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Invoice Print View ───────────────────────────────────────────────────────
function InvoiceView({ customerId, invoiceId, onClose }) {
  const [doc, setDoc]           = useState(null);
  const [err, setErr]           = useState('');
  const [downloading, setDownloading] = useState(false);
  const [paying, setPaying]     = useState(false);
  const docRef = useRef(null);
  const INV_BLUE = '#1b7abf';

  const loadInvoice = useCallback(() => {
    fetch(`${BACKEND_URL}/api/customer/${customerId}/invoice/${invoiceId}`)
      .then(r => r.json())
      .then(d => { if (d.data) setDoc(d.data); else setErr('Could not load invoice.'); })
      .catch(() => setErr('Could not load invoice.'));
  }, [customerId, invoiceId]);

  useEffect(() => { loadInvoice(); }, [loadInvoice]);

  const downloadPdf = async () => {
    if (!docRef.current || !doc) return;
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      await html2pdf().set({
        margin: 0, filename: `Invoice-${invoiceId}.pdf`,
        image: { type: 'jpeg', quality: 0.97 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      }).from(docRef.current).save();
    } finally { setDownloading(false); }
  };

  const handlePrint = () => {
    if (!docRef.current) return;
    const wrapper = document.createElement('div');
    wrapper.id = 'inv-print-wrapper';
    wrapper.innerHTML = docRef.current.outerHTML;
    document.body.appendChild(wrapper);
    document.body.classList.add('printing-invoice');
    window.print();
    document.body.removeChild(wrapper);
    document.body.classList.remove('printing-invoice');
  };

  const items = doc?.items || [];
  const taxes = doc?.taxes || [];

  return (
    <div className="fixed inset-0 z-50 bg-gray-100 overflow-y-auto text-gray-900">
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body > * { display: none !important; }
          #inv-print-wrapper { display: block !important; background: white; }
          #inv-print-doc { min-height: auto !important; }
          #inv-print-doc .p-6-inner { min-height: 9in !important; }
        }
      `}</style>

      {/* Action bar */}
      <div className="no-print sticky top-0 bg-white shadow-md z-40 px-6 py-3 flex items-center justify-between">
        <button onClick={onClose} className="text-gray-600 hover:text-gray-900 text-sm font-medium">← Back</button>
        <div className="flex gap-3">
          {doc && Number(doc.outstanding_amount) > 0 && doc.status !== 'Paid' && (
            <button onClick={() => setPaying(true)}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white uppercase tracking-wider shadow hover:opacity-90"
              style={{ background: '#ea580c' }}
              data-testid="pay-now-button">
              💳 Pay ${Number(doc.outstanding_amount).toFixed(2)}
            </button>
          )}
          <button onClick={downloadPdf} disabled={downloading || !doc}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-50">
            {downloading ? '⏳ Generating…' : '⬇ Download PDF'}
          </button>
          <button onClick={handlePrint}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
            🖨 Print
          </button>
        </div>
      </div>

      {paying && doc && (
        <PaymentModal
          invoice={{ name: doc.name, outstanding_amount: doc.outstanding_amount, customer_email: doc.contact_email || '', customer_name: doc.customer_name || '' }}
          backendUrl={BACKEND_URL}
          onClose={() => setPaying(false)}
          onSuccess={() => { setPaying(false); loadInvoice(); }}
        />
      )}

      {!doc && !err && (
        <div className="flex items-center justify-center py-24 text-gray-400 text-sm gap-3">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
          Loading invoice…
        </div>
      )}
      {err && <div className="flex items-center justify-center py-24 text-rose-500 text-sm">{err}</div>}

      {doc && (
        <div className="inv-outer py-6 px-4">
          <div id="inv-print-doc" ref={docRef} className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden relative" style={{ minHeight: '11in', display: 'flex', flexDirection: 'column' }}>

            {/* PAID stamp */}
            {doc.status === 'Paid' && (
              <div className="no-print absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none -rotate-12">
                <div className="border-8 border-emerald-600 rounded-lg px-8 py-3" style={{ opacity: 0.5 }}>
                  <p className="text-5xl font-black text-emerald-600 tracking-widest">PAID</p>
                </div>
              </div>
            )}

            <div className="p-6 p-6-inner" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <div className="mb-4">
                <img src="/header_new.png" alt="AAA Irrigation Service" style={{ display: 'block', width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 4 }} />
                <div className="text-center py-2 border-b-4 mt-1" style={{ borderColor: INV_BLUE }}>
                  <p className="text-xs font-semibold text-gray-700">14 Monroe Ct. Allen TX, 75002 · (469) 751-3567 · mail@aaairrigationservice.com</p>
                </div>
              </div>

              {/* Customer + invoice meta */}
              <div className="flex justify-between mb-4">
                <div className="text-sm space-y-0.5">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Bill To</p>
                  <p className="text-lg font-bold" style={{ color: INV_BLUE }}>{doc.customer_name}</p>
                  {doc.address_display && <p className="text-gray-600 text-xs whitespace-pre-line">{addressLines(doc.address_display)}</p>}
                  {doc.contact_mobile && <p className="text-gray-600 text-xs">{doc.contact_mobile}</p>}
                  {doc.contact_email  && <p className="text-gray-600 text-xs">{doc.contact_email}</p>}
                </div>
                <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: '0 3px' }}>
                  <tbody>
                    <tr><td className="text-right text-gray-500 pr-3 whitespace-nowrap">Invoice #:</td><td className="font-bold whitespace-nowrap" style={{ color: INV_BLUE }}>{doc.name}</td></tr>
                    <tr><td className="text-right text-gray-500 pr-3 whitespace-nowrap">Date:</td><td className="font-medium whitespace-nowrap">{fmtDate(doc.posting_date)}</td></tr>
                    <tr><td className="text-right text-gray-500 pr-3 whitespace-nowrap">Due:</td><td className="font-medium whitespace-nowrap">{fmtDate(doc.due_date)}</td></tr>
                    <tr>
                      <td className="text-right text-gray-500 pr-3 whitespace-nowrap align-middle">Status:</td>
                      <td className="align-middle">
                        <span style={{ background: doc.status === 'Paid' ? '#d1fae5' : '#fff7ed', color: doc.status === 'Paid' ? '#065f46' : '#9a3412', border: `1px solid ${doc.status === 'Paid' ? '#6ee7b7' : '#fb923c'}`, borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{doc.status}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Line items */}
              {items.length > 0 && (
                <table className="w-full mb-4 text-sm">
                  <thead>
                    <tr style={{ background: INV_BLUE }} className="text-white">
                      <th className="py-2 px-3 text-left font-semibold text-xs">Zone</th>
                      <th className="py-2 px-3 text-left font-semibold text-xs">Description</th>
                      <th className="py-2 px-3 text-center font-semibold text-xs w-12">Qty</th>
                      <th className="py-2 px-3 text-right font-semibold text-xs w-20">Rate</th>
                      <th className="py-2 px-3 text-right font-semibold text-xs w-24">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="py-2 px-3 border-b border-gray-200 text-xs text-gray-600">{item.custom_zone || ''}</td>
                        <td className="py-2 px-3 border-b border-gray-200 text-xs font-medium text-gray-800">{item.item_name || item.item_code}</td>
                        <td className="py-2 px-3 border-b border-gray-200 text-xs text-center text-gray-700">{item.qty}</td>
                        <td className="py-2 px-3 border-b border-gray-200 text-xs text-right text-gray-700">{fmtCurrency(item.rate)}</td>
                        <td className="py-2 px-3 border-b border-gray-200 text-xs text-right font-semibold">{fmtCurrency(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Totals */}
              <div className="flex justify-end mb-4">
                <div className="w-56 text-sm">
                  <div className="flex justify-between py-1.5 border-b border-gray-200">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">{fmtCurrency(doc.net_total)}</span>
                  </div>
                  {taxes.map((t, i) => (
                    <div key={i} className="flex justify-between py-1.5 border-b border-gray-200">
                      <span className="text-gray-600">{t.description || 'TX Sales Tax 8.25%'}</span>
                      <span className="font-medium">{fmtCurrency(t.tax_amount)}</span>
                    </div>
                  ))}
                  {doc.discount_amount > 0 && (
                    <div className="flex justify-between py-1.5 border-b border-gray-200">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-red-500">-{fmtCurrency(doc.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 text-white px-3 rounded mt-1" style={{ background: INV_BLUE }}>
                    <span className="font-bold">TOTAL:</span>
                    <span className="font-bold">{fmtCurrency(doc.grand_total)}</span>
                  </div>
                </div>
              </div>

              {/* Pay / Zelle buttons */}
              {Number(doc.outstanding_amount) > 0 && doc.status !== 'Paid' && (
                <div className="no-print flex gap-3 mt-6">
                  <button
                    onClick={() => setPaying(true)}
                    className="flex-1 py-3 rounded-xl text-white font-bold text-sm uppercase tracking-wider shadow hover:opacity-90 transition"
                    style={{ background: '#ea580c' }}
                  >
                    💳 Pay ${Number(doc.outstanding_amount).toFixed(2)}
                  </button>
                  <button
                    onClick={() => setPaying(true)}
                    className="flex-1 py-3 rounded-xl font-bold text-sm uppercase tracking-wider shadow hover:opacity-90 transition text-white"
                    style={{ background: '#6D1ED4' }}
                  >
                    ⚡ Pay with Zelle
                  </button>
                </div>
              )}

              {/* Footer */}
              <div className="text-center pt-3 border-t border-gray-300 mt-4" style={{ marginTop: Number(doc.outstanding_amount) > 0 && doc.status !== 'Paid' ? '1rem' : 'auto' }}>
                <p className="text-xs text-gray-500 italic">Irrigation in Texas is regulated by the TCEQ, P.O. Box 13087, Austin, Texas 78711-3087</p>
                <p className="text-xs font-semibold mt-0.5" style={{ color: '#00cc44' }}>Powered by Boatman Systems™</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoicesSection({ customerId }) {
  const { selected } = useServiceLocation();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    const url = `${BACKEND_URL}/api/customer/${customerId}/invoices` +
      (selected ? `?service_location=${encodeURIComponent(selected)}` : '');
    fetch(url)
      .then(r => r.json())
      .then(d => setData(d.data || []))
      .catch(() => setData([]));
  }, [customerId, selected]);

  const invoices = data || [];
  const goToInvoice = (name) => {
    window.location.href = `/invoice?id=${name}&customer=${encodeURIComponent(customerId)}`;
  };

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-5">Invoices</p>
      {data === null ? (
        <div className="flex items-center gap-3 text-sm text-slate-400 py-2">
          <div className="w-4 h-4 border border-slate-500 border-t-cyan-400 rounded-full animate-spin" />
          Loading invoices...
        </div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-slate-400">No invoices on file yet.</p>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => {
            const unpaid = Number(inv.outstanding_amount) > 0 && inv.status !== 'Paid';
            return (
              <div key={inv.name} className="rounded-[20px] border border-white/8 bg-white/5 p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-white">{inv.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {fmtDate(inv.posting_date)}
                      {inv.due_date ? ` · Due ${fmtDate(inv.due_date)}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-white">{fmtCurrency(inv.grand_total)}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  {unpaid && (
                    <button
                      onClick={() => goToInvoice(inv.name)}
                      className="flex-1 py-2 rounded-xl text-white font-bold text-sm uppercase tracking-wider shadow hover:opacity-90 transition"
                      style={{ background: '#ea580c' }}
                    >
                      💳 Pay {fmtCurrency(inv.outstanding_amount)}
                    </button>
                  )}
                  <button
                    onClick={() => goToInvoice(inv.name)}
                    className="px-4 py-2 rounded-xl text-white text-xs font-semibold hover:opacity-90 transition"
                    style={{ background: '#1b7abf' }}
                  >
                    View / Print
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Running Zone Progress Bar ────────────────────────────────────────────────

function RunningZoneBar({ runState, onStop, stopBusy, onNext, onBack, canGoBack, navBusy }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!runState) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now() - runState.startedAt) / 1000));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [runState]);

  if (!runState) return null;

  const { zoneName, zoneNumber, duration } = runState;
  const pct = Math.min(100, Math.round((elapsed / duration) * 100));
  const remaining = Math.max(0, duration - elapsed);
  const remMin = Math.floor(remaining / 60);
  const remSec = String(remaining % 60).padStart(2, '0');
  const done = remaining === 0;

  return (
    <div className="rounded-[20px] border border-cyan-400/30 bg-cyan-950/60 p-4 mb-5 shadow-lg">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {done
            ? <span className="text-lg">✅</span>
            : <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-400" />
              </span>
          }
          <span className="text-sm font-bold text-white">
            {done ? 'Run Complete' : `Running · Zone ${zoneNumber}`}
          </span>
          <span className="text-sm text-cyan-200/80 font-medium">{zoneName}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono text-cyan-300 tabular-nums">
            {done ? '0:00' : `${remMin}:${remSec}`}
          </span>
          <button
            onClick={onBack}
            disabled={!canGoBack || navBusy || stopBusy}
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-cyan-200 hover:bg-white/10 transition disabled:opacity-30"
          >
            ⏮ Back
          </button>
          <button
            onClick={onNext}
            disabled={navBusy || stopBusy}
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-cyan-200 hover:bg-white/10 transition disabled:opacity-50"
          >
            {navBusy ? '…' : '⏭ Next'}
          </button>
          <button
            onClick={onStop}
            disabled={stopBusy}
            className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-3 py-1 text-xs font-semibold text-rose-300 hover:bg-rose-400/20 transition disabled:opacity-50"
          >
            {stopBusy ? '…' : '⏹ Stop'}
          </button>
        </div>
      </div>

      {/* Progress bar track */}
      <div className="relative h-4 rounded-full bg-white/8 overflow-hidden border border-white/5">
        {/* Animated shimmer fill */}
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-none"
          style={{
            width: `${pct}%`,
            background: done
              ? 'linear-gradient(90deg, #059669, #10b981)'
              : 'linear-gradient(90deg, #1094ff, #19d3b3, #1094ff)',
            backgroundSize: '200% 100%',
            animation: done ? 'none' : 'shimmer 2s linear infinite',
          }}
        />
        {/* Percentage label inside bar */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[10px] font-bold text-white/80 drop-shadow">{pct}%</span>
        </div>
      </div>

      {/* Zone count label */}
      <p className="text-xs text-slate-400 mt-2">
        {done
          ? `Finished — ${Math.round(duration / 60)} min run`
          : `${Math.round(duration / 60)} min scheduled · ${pct}% complete`
        }
      </p>

      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </div>
  );
}

// ── Zone Controls Section ───────────────────────────────────────────────────

function ZoneControls({ zones, customerId, onRefresh, onOpenDetail, zoneMetrics, selected }) {
  // runState = { zoneId, zoneName, zoneNumber, duration, startedAt, queueIndex } | null
  // queueIndex is this zone's position in enabledZones at the moment the
  // sequence reached it -- Next/Back walk that same list forward/backward,
  // reusing the single-zone start/stop calls that already existed.
  const [runState, setRunState] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState({});
  const [busy, setBusy] = useState(null); // zone_id or 'stop_all'
  const [message, setMessage] = useState(null);

  // Auto-clear runState when timer naturally expires
  useEffect(() => {
    if (!runState) return;
    const remaining = runState.duration * 1000 - (Date.now() - runState.startedAt);
    if (remaining <= 0) { setRunState(null); return; }
    const id = setTimeout(() => setRunState(null), remaining + 1000);
    return () => clearTimeout(id);
  }, [runState]);

  const enabledZones = zones.filter(z => z.enabled);

  const stopAll = async () => {
    setBusy('stop_all');
    setMessage(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/rachio/device/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          ...(selected ? { service_location_id: selected } : {}),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to stop');
      }
      setRunState(null);
      setMessage({ type: 'success', text: 'All watering stopped' });
      if (onRefresh) setTimeout(onRefresh, 2000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  // Starts the zone at this position in enabledZones and enters/advances the
  // running sequence. Shared by starting a zone from the list, Next, and Back
  // -- all three are "run the zone at this index" with a different index.
  const runZoneAtIndex = async (index) => {
    const zone = enabledZones[index];
    if (!zone) { await stopAll(); return; }
    const dur = selectedDuration[zone.id] || 300;
    setBusy(zone.id);
    setMessage(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/rachio/zone/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          zone_id: zone.id,
          duration_seconds: dur,
          ...(selected ? { service_location_id: selected } : {}),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to start zone');
      }
      setRunState({ zoneId: zone.id, zoneName: zone.name, zoneNumber: zone.number, duration: dur, startedAt: Date.now(), queueIndex: index });
      setMessage(null);
      if (onRefresh) setTimeout(onRefresh, 2000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const startZone = (zone) => {
    const index = enabledZones.findIndex(z => z.id === zone.id);
    runZoneAtIndex(index === -1 ? 0 : index);
  };

  // Next past the last zone ends the sequence (same as Stop), matching how
  // a Rachio Quick Run naturally finishes.
  const nextZone = () => {
    if (!runState) return;
    runZoneAtIndex(runState.queueIndex + 1);
  };

  // Back is disabled on the first zone (RunningZoneBar hides/disables the
  // button itself), so this is only ever called with queueIndex > 0.
  const backZone = () => {
    if (!runState || runState.queueIndex <= 0) return;
    runZoneAtIndex(runState.queueIndex - 1);
  };

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">Zone Controls</p>
        <button
          onClick={stopAll}
          disabled={busy === 'stop_all'}
          className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-2 text-sm font-semibold text-rose-300 transition hover:bg-rose-400/20 disabled:opacity-50"
        >
          {busy === 'stop_all' ? 'Stopping...' : 'Stop All Watering'}
        </button>
      </div>

      {/* Live running zone progress bar */}
      <RunningZoneBar
        runState={runState}
        onStop={stopAll}
        stopBusy={busy === 'stop_all'}
        onNext={nextZone}
        onBack={backZone}
        canGoBack={!!runState && runState.queueIndex > 0}
        navBusy={busy !== null && busy !== 'stop_all'}
      />

      {message && (
        <div className={`rounded-xl px-4 py-3 text-sm mb-4 ${message.type === 'success' ? 'bg-emerald-400/10 text-emerald-300' : 'bg-rose-400/10 text-rose-300'}`}>
          {message.text}
        </div>
      )}

      <div className="space-y-3">
        {enabledZones.map(zone => (
          <ZoneCard
            key={zone.id}
            zone={zone}
            running={runState?.zoneId === zone.id}
            busy={busy === zone.id}
            selectedDuration={selectedDuration[zone.id] || 300}
            onDurationChange={val => setSelectedDuration(prev => ({ ...prev, [zone.id]: val }))}
            onStart={() => startZone(zone)}
            onOpenDetail={() => onOpenDetail && onOpenDetail(zone)}
            zoneMetrics={zoneMetrics}
          />
        ))}
      </div>
    </div>
  );
}

// ── Shared photo loader hook ──────────────────────────────────────────────────

function useZonePhoto(zone) {
  const [photo, setPhoto] = useState(zone.imageUrl || null);

  useEffect(() => {
    // Zone photos are owner-or-staff since 2026-10-15: the portal session is attached
    // automatically; in admin mode also send the stored admin key (never prompts here).
    let adminKey = null;
    try { adminKey = sessionStorage.getItem('admin_key'); } catch (e) { /* storage unavailable */ }
    fetch(`${BACKEND_URL}/api/rachio/zone/${zone.id}/photo`, adminKey ? { headers: { 'X-Admin-Key': adminKey } } : undefined)
      .then(r => r.json())
      .then(d => { if (d.photo_data) setPhoto(d.photo_data); })
      .catch(() => {});
  }, [zone.id]);

  const compressImage = (file, callback) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Scale down if needed
        const maxDimension = 1600;
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Compress until under 3MB
        let quality = 0.75;
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (blob.size <= 3_000_000 || quality <= 0.05) {
                const reader2 = new FileReader();
                reader2.onload = (e) => callback(e.target.result);
                reader2.readAsDataURL(blob);
              } else {
                quality -= 0.1;
                tryCompress();
              }
            },
            'image/jpeg',
            quality
          );
        };
        tryCompress();
      };
    };
  };

  const upload = (file, onDone) => {
    if (!file) return;
    if (file.size > 3_000_000) {
      compressImage(file, async (compressedData) => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/rachio/zone/photo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zone_id: zone.id, photo_data: compressedData }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Upload failed');
          setPhoto(compressedData);
          onDone && onDone(compressedData);
        } catch (err) { alert('Upload failed: ' + err.message); }
      });
    } else {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/rachio/zone/photo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zone_id: zone.id, photo_data: ev.target.result }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Upload failed');
          setPhoto(ev.target.result);
          onDone && onDone(ev.target.result);
        } catch (err) { alert('Upload failed: ' + err.message); }
      };
      reader.readAsDataURL(file);
    }
  };

  return { photo, setPhoto, upload };
}

// ── Zone Detail Modal ────────────────────────────────────────────────────────

function ZoneDetailModal({ zone, customerId, onClose, zoneMetrics, selected }) {
  const { photo, upload } = useZonePhoto(zone);
  const [duration, setDuration] = useState(300);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const startZone = async () => {
    setRunning(true); setMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/rachio/zone/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          zone_id: zone.id,
          duration_seconds: duration,
          ...(selected ? { service_location_id: selected } : {}),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      setMsg({ ok: true, text: `Running for ${Math.round(duration / 60)} min` });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally { setRunning(false); }
  };

  const stopAll = async () => {
    setRunning(true); setMsg(null);
    try {
      await fetch(`${BACKEND_URL}/api/rachio/device/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          ...(selected ? { service_location_id: selected } : {}),
        }),
      });
      setMsg({ ok: true, text: 'Watering stopped' });
    } catch { setMsg({ ok: false, text: 'Failed to stop' }); }
    finally { setRunning(false); }
  };

  const handleFile = (e) => {
    setUploading(true);
    upload(e.target.files[0], () => setUploading(false));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setUploading(true);
      upload(files[0], () => setUploading(false));
    }
  };

  // Close on backdrop click or Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full sm:max-w-lg rounded-t-[32px] sm:rounded-[32px] bg-[#0b1e30] shadow-2xl overflow-hidden"
           onClick={e => e.stopPropagation()}>

        {/* Hero photo */}
        <div className="relative w-full h-64 sm:h-72 bg-slate-900"
             onDragOver={handleDragOver}
             onDragLeave={handleDragLeave}
             onDrop={handleDrop}>
          {photo
            ? <img src={photo} alt={zone.name} className="w-full h-full object-cover" onError={() => {}} />
            : <div className="w-full h-full flex flex-col items-center justify-center text-slate-600">
                <span className="text-7xl">🌿</span>
                <p className="mt-3 text-sm">No photo yet</p>
              </div>
          }
          {/* Drag overlay */}
          {dragOver && (
            <div className="absolute inset-0 bg-cyan-400/20 border-2 border-dashed border-cyan-300 flex items-center justify-center">
              <div className="text-center">
                <span className="text-6xl">📤</span>
                <p className="text-sm font-semibold text-cyan-100 mt-2">Drop photo here</p>
              </div>
            </div>
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b1e30] via-transparent to-transparent" />

          {/* Close button */}
          <button onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white text-lg hover:bg-black/70 transition">
            ✕
          </button>

          {/* Change photo button */}
          <button onClick={() => fileRef.current?.click()}
            className="absolute bottom-4 right-4 rounded-xl bg-black/50 backdrop-blur border border-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-black/70 transition">
            {uploading ? 'Uploading...' : '📷 Change Photo'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

          {/* Zone name over photo */}
          <div className="absolute bottom-4 left-4">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-300/80">Zone {zone.number}</p>
            <h2 className="text-2xl font-bold text-white mt-0.5">{zone.name}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Status', value: zone.status },
              { label: 'Runtime', value: zone.runtime },
              { label: 'Flow', value: zoneFlowGallonsLabel(zoneMetrics, zone) },
            ].map(s => (
              <div key={s.label} className="rounded-[16px] border border-white/8 bg-white/5 p-3 text-center">
                <p className="text-xs text-slate-400">{s.label}</p>
                <p className="text-sm font-semibold text-white mt-0.5">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Feedback message */}
          {msg && (
            <div className={`rounded-xl px-4 py-3 text-sm ${msg.ok ? 'bg-emerald-400/10 text-emerald-300' : 'bg-rose-400/10 text-rose-300'}`}>
              {msg.text}
            </div>
          )}

          {/* Run controls */}
          <div className="rounded-[20px] border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-200/70">Manual Run</p>
              <span className="text-lg font-bold text-white tabular-nums">{Math.round(duration / 60)} <span className="text-sm font-normal text-slate-400">min</span></span>
            </div>
            <input
              type="range" min={60} max={3600} step={60}
              value={duration}
              onChange={e => setDuration(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer mb-1"
              style={{ accentColor: '#19d3b3' }}
            />
            <div className="flex justify-between text-xs text-slate-500 mb-4">
              <span>1 min</span><span>30 min</span><span>60 min</span>
            </div>
            <button onClick={startZone} disabled={running}
              className="w-full rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] py-3 text-sm font-bold text-white shadow-lg disabled:opacity-50 transition hover:translate-y-[-1px]">
              {running ? 'Starting...' : `▶ Run Zone ${zone.number}`}
            </button>
          </div>

          <button onClick={stopAll} disabled={running}
            className="w-full rounded-xl border border-rose-400/30 bg-rose-400/10 py-3 text-sm font-semibold text-rose-300 hover:bg-rose-400/20 transition disabled:opacity-50">
            ⏹ Stop All Watering
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Zone Card with Photo ─────────────────────────────────────────────────────

function ZoneCard({ zone, running, busy, selectedDuration, onDurationChange, onStart, onOpenDetail, zoneMetrics }) {
  const { photo } = useZonePhoto(zone);

  return (
    <div className={`rounded-[20px] border p-4 ${running ? 'border-cyan-400/40 bg-cyan-400/10' : 'border-white/8 bg-white/5'}`}>
      <div className="flex items-center gap-3 flex-wrap">
        {/* Zone photo — click opens detail modal */}
        <button onClick={onOpenDetail} className="shrink-0 focus:outline-none">
          {photo
            ? <img src={photo} alt={zone.name} className="w-16 h-16 rounded-xl object-cover border border-white/10 hover:opacity-90 transition" />
            : <div className="w-16 h-16 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl hover:bg-white/10 transition">🌿</div>
          }
        </button>

        {/* Zone info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full shrink-0 ${running ? 'bg-cyan-400 animate-pulse' : 'bg-slate-500'}`} />
            <button onClick={onOpenDetail} className="text-sm font-semibold text-white truncate hover:text-cyan-300 transition text-left">
              {zone.name}
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">Zone {zone.number} · {zone.runtime} · {zoneFlowGallonsLabel(zoneMetrics, zone)}</p>
        </div>

        {/* Quick controls */}
        <div className="flex flex-col gap-1.5 shrink-0 min-w-[140px]">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Duration</span>
            <span className="text-xs font-bold text-white tabular-nums">{Math.round(selectedDuration / 60)} min</span>
          </div>
          <input
            type="range" min={60} max={3600} step={60}
            value={selectedDuration}
            onChange={e => onDurationChange(Number(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: '#19d3b3' }}
          />
          <button onClick={onStart} disabled={busy}
            className="rounded-lg bg-[linear-gradient(90deg,#1094ff,#19d3b3)] px-4 py-1.5 text-xs font-bold text-white shadow disabled:opacity-50 transition hover:translate-y-[-1px]">
            {busy ? '...' : 'Run'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Schedule Manager (toggle + delete + create) ──────────────────────────────

const ALL_DAYS = ['MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY','SUNDAY'];
const DAY_SHORT = { MONDAY:'Mon',TUESDAY:'Tue',WEDNESDAY:'Wed',THURSDAY:'Thu',FRIDAY:'Fri',SATURDAY:'Sat',SUNDAY:'Sun' };

function ScheduleToggles({ schedules, customerId, zones, onRefresh, isAdmin }) {
  const [localSchedules, setLocalSchedules] = useState(schedules);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // New schedule form state
  const [newName, setNewName] = useState('');
  const [newHour, setNewHour] = useState(6);
  const [newMinute, setNewMinute] = useState(0);
  const [newDays, setNewDays] = useState(['MONDAY','WEDNESDAY','FRIDAY']);
  const [newZones, setNewZones] = useState({});  // {zone_id: duration_seconds}
  const [creating, setCreating] = useState(false);

  useEffect(() => { setLocalSchedules(schedules); }, [schedules]);

  const deleteSchedule = async (scheduleId, name) => {
    if (!window.confirm(`Delete schedule "${name}"? This cannot be undone.`)) return;
    setBusy(scheduleId + '_del');
    setMessage(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/rachio/schedule/${scheduleId}?customer_id=${encodeURIComponent(customerId)}`,
        { method: 'DELETE', headers: { 'X-Admin-Key': getAdminKey() } }
      );
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setLocalSchedules(prev => prev.filter(s => s.id !== scheduleId));
      setMessage({ type: 'success', text: `"${name}" deleted` });
      if (onRefresh) setTimeout(onRefresh, 2000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(null);
    }
  };

  const createSchedule = async () => {
    if (!newName.trim()) { setMessage({ type: 'error', text: 'Name required' }); return; }
    const zoneList = Object.entries(newZones)
      .filter(([, dur]) => dur > 0)
      .map(([zone_id, dur]) => ({ zone_id, duration_seconds: dur }));
    if (zoneList.length === 0) { setMessage({ type: 'error', text: 'Select at least one zone' }); return; }
    if (newDays.length === 0) { setMessage({ type: 'error', text: 'Select at least one day' }); return; }

    setCreating(true);
    setMessage(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/rachio/schedule/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': getAdminKey() },
        body: JSON.stringify({
          customer_id: customerId,
          name: newName.trim(),
          start_hour: newHour,
          start_minute: newMinute,
          days_of_week: newDays,
          zones: zoneList,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setMessage({ type: 'success', text: `Schedule "${newName}" created!` });
      setShowCreate(false);
      setNewName(''); setNewDays(['MONDAY','WEDNESDAY','FRIDAY']); setNewZones({});
      if (onRefresh) setTimeout(onRefresh, 2000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCreating(false);
    }
  };

  const toggleDay = (day) => setNewDays(prev =>
    prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
  );

  const timeLabel = `${String(newHour).padStart(2,'0')}:${String(newMinute).padStart(2,'0')}`;

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">Schedules</p>
        {isAdmin && (
          <button
            onClick={() => setShowCreate(v => !v)}
            className="rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold text-cyan-200 hover:bg-cyan-400/20 transition"
          >
            {showCreate ? 'Cancel' : '+ New Schedule'}
          </button>
        )}
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 text-sm mb-4 ${message.type === 'success' ? 'bg-emerald-400/10 text-emerald-300' : 'bg-rose-400/10 text-rose-300'}`}>
          {message.text}
        </div>
      )}

      {/* Existing schedules */}
      <div className="space-y-3">
        {localSchedules.length === 0 && <p className="text-sm text-slate-400">No schedules yet.</p>}
        {localSchedules.map(s => (
          <div key={s.id} className="rounded-[20px] border border-white/8 bg-white/5 p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${s.enabled ? 'bg-emerald-400' : 'bg-slate-500'}`} />
              <span className="text-sm font-medium text-white truncate">{s.name || 'Schedule'}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Status indicator (read-only — Rachio API doesn't support enable/disable) */}
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${s.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-600/40 text-slate-400'}`}>
                {s.enabled ? 'On' : 'Off'}
              </span>
              {/* Delete (admin only) */}
              {isAdmin && (
                <button
                  onClick={() => deleteSchedule(s.id, s.name || 'Schedule')}
                  disabled={!!busy}
                  className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-2.5 py-1.5 text-xs text-rose-300 hover:bg-rose-400/20 transition disabled:opacity-50"
                >
                  🗑
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create new schedule form (admin only) */}
      {showCreate && isAdmin && (
        <div className="mt-5 rounded-[20px] border border-cyan-400/20 bg-cyan-400/5 p-5 space-y-4">
          <p className="text-sm font-semibold text-cyan-100">New Schedule</p>

          <input
            type="text"
            placeholder="Schedule name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-400/50 focus:outline-none"
          />

          <div className="flex gap-3 items-center">
            <label className="text-xs text-slate-400 shrink-0">Start time</label>
            <input
              type="number" min="0" max="23" value={newHour}
              onChange={e => setNewHour(Number(e.target.value))}
              className="w-16 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white text-center focus:outline-none"
            />
            <span className="text-slate-400">:</span>
            <input
              type="number" min="0" max="59" step="5" value={newMinute}
              onChange={e => setNewMinute(Number(e.target.value))}
              className="w-16 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white text-center focus:outline-none"
            />
            <span className="text-xs text-slate-400">{timeLabel} local</span>
          </div>

          <div>
            <p className="text-xs text-slate-400 mb-2">Days</p>
            <div className="flex gap-1.5 flex-wrap">
              {ALL_DAYS.map(day => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${newDays.includes(day) ? 'bg-cyan-500 text-white' : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10'}`}
                >
                  {DAY_SHORT[day]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs text-slate-400 mb-2">Zones & Duration (minutes)</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(zones || []).filter(z => z.enabled).map(z => (
                <div key={z.id} className="flex items-center gap-3">
                  <span className="text-xs text-slate-300 flex-1 truncate">Zone {z.number} · {z.name}</span>
                  <input
                    type="number" min="0" max="60" placeholder="0"
                    value={Math.round((newZones[z.id] || 0) / 60) || ''}
                    onChange={e => {
                      const mins = Number(e.target.value);
                      setNewZones(prev => ({ ...prev, [z.id]: mins * 60 }));
                    }}
                    className="w-16 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-white text-center focus:outline-none"
                  />
                  <span className="text-xs text-slate-500">min</span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={createSchedule}
            disabled={creating}
            className="w-full rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] py-2.5 text-sm font-bold text-white shadow disabled:opacity-50 transition hover:translate-y-[-1px]"
          >
            {creating ? 'Creating...' : 'Create Schedule'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Water Usage Dashboard ───────────────────────────────────────────────────

function WaterUsageDashboard({ customerId }) {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/rachio/usage/${customerId}?period=${period}`);
      if (!res.ok) throw new Error('Failed to load usage data');
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [customerId, period]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const periods = [
    { key: 'day', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
  ];

  // Calculate average inches across zones that have data
  const avgInches = data?.zones
    ? (() => {
        const withData = data.zones.filter(z => z.total_inches != null);
        if (withData.length === 0) return null;
        return (withData.reduce((s, z) => s + z.total_inches, 0) / withData.length).toFixed(2);
      })()
    : null;

  // Find max gallons for bar chart scaling
  const maxDaily = data?.daily ? Math.max(...data.daily.map(d => d.gallons), 1) : 1;

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">Water Usage</p>
        <div className="flex rounded-xl border border-white/10 overflow-hidden">
          {periods.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-4 py-2 text-xs font-semibold transition ${period === p.key ? 'bg-cyan-400/20 text-cyan-100' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-sm text-slate-400 py-6 justify-center">
          <div className="w-4 h-4 border border-slate-500 border-t-cyan-400 rounded-full animate-spin" />
          Loading usage data...
        </div>
      ) : error ? (
        <p className="text-sm text-rose-300 bg-rose-400/10 rounded-xl px-4 py-3">{error}</p>
      ) : data ? (
        <>
          {/* Period label */}
          <p className="text-sm text-slate-300 mb-4">{data.period_label}</p>

          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="rounded-[20px] border border-white/8 bg-white/5 p-4 text-center">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Total Gallons</p>
              <p className="text-2xl font-bold text-white">
                {data.total_gallons != null ? data.total_gallons.toLocaleString() : '--'}
              </p>
            </div>
            <div className="rounded-[20px] border border-white/8 bg-white/5 p-4 text-center">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Avg Inches Applied</p>
              <p className="text-2xl font-bold text-white">
                {avgInches != null ? `${avgInches}"` : '--'}
              </p>
            </div>
          </div>

          {/* Zone breakdown table */}
          {data.zones && data.zones.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-3">Zone Breakdown</p>
              <div className="overflow-hidden rounded-[18px] border border-white/8">
                <table className="w-full text-left text-sm">
                  <thead className="bg-white/5 text-slate-400">
                    <tr>
                      <th className="px-4 py-3 font-medium">Zone</th>
                      <th className="px-4 py-3 font-medium text-right">Gallons</th>
                      <th className="px-4 py-3 font-medium text-right">Inches</th>
                      <th className="px-4 py-3 font-medium text-right">Runs</th>
                      <th className="px-4 py-3 font-medium text-right">Minutes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.zones.map((z, i) => {
                      const noData = z.total_gallons == null;
                      return (
                        <tr key={i} className={`border-t border-white/8 ${noData ? 'bg-slate-800/30 opacity-60' : 'bg-slate-900/20'}`}>
                          <td className="px-4 py-3 text-white font-medium">
                            {z.zone_name}
                            {noData && <span className="ml-2 text-xs text-slate-500">(no GPM)</span>}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-200">
                            {noData ? '--' : z.total_gallons.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-200">
                            {noData || z.total_inches == null ? '--' : `${z.total_inches}"`}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-200">{z.runs}</td>
                          <td className="px-4 py-3 text-right text-slate-200">{z.total_minutes}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily trend bar chart */}
          {data.daily && data.daily.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-3">Daily Trend</p>
              <div className="flex items-end gap-1 h-32">
                {data.daily.map((d, i) => {
                  const pct = Math.max(4, (d.gallons / maxDaily) * 100);
                  const dateLabel = d.date.slice(5); // MM-DD
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                      <span className="text-[10px] text-slate-400 truncate">{d.gallons > 0 ? Math.round(d.gallons) : ''}</span>
                      <div
                        className="w-full rounded-t-md bg-gradient-to-t from-cyan-500/60 to-cyan-300/40 min-h-[4px] transition-all"
                        style={{ height: `${pct}%` }}
                        title={`${d.date}: ${d.gallons} gal`}
                      />
                      <span className="text-[9px] text-slate-500 truncate">{dateLabel}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data.zones && data.zones.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No watering activity recorded for this period.</p>
          )}

          {/* Footer note */}
          <p className="text-[11px] text-slate-500 mt-4 text-center leading-4">
            Usage estimates based on actual zone runtimes and GPM measured by AAA Irrigation Service
          </p>
        </>
      ) : null}
    </div>
  );
}

// ── Admin Zone Toggles ───────────────────────────────────────────────────────

function AdminZoneToggles({ zones, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);

  const toggle = async (zoneId, currentEnabled) => {
    setBusy(zoneId);
    setMessage(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/rachio/zone/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': getAdminKey() },
        body: JSON.stringify({ zone_id: zoneId, enabled: !currentEnabled }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      setMessage({ type: 'success', text: `Zone ${!currentEnabled ? 'enabled' : 'disabled'}` });
      setTimeout(() => { onRefresh(); setMessage(null); }, 1200);
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-4">Zone Enable / Disable</p>
      {message && (
        <p className={`mb-3 text-xs font-semibold ${message.type === 'success' ? 'text-emerald-300' : 'text-rose-300'}`}>{message.text}</p>
      )}
      <div className="space-y-2">
        {zones.map(zone => (
          <div key={zone.id} className="flex items-center justify-between rounded-[16px] border border-white/8 bg-white/5 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-white">Zone {zone.number} · {zone.name}</p>
              <p className={`text-xs mt-0.5 ${zone.enabled ? 'text-emerald-300' : 'text-slate-500'}`}>{zone.enabled ? 'Enabled' : 'Disabled'}</p>
            </div>
            <button
              onClick={() => toggle(zone.id, zone.enabled)}
              disabled={busy === zone.id}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${zone.enabled ? 'bg-emerald-500' : 'bg-slate-600'} ${busy === zone.id ? 'opacity-50' : ''}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${zone.enabled ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Admin Zone Config Editor ────────────────────────────────────────────────

function GpmCalculator({ configs, onApply }) {
  const [open, setOpen] = useState(false);
  const [startReading, setStartReading] = useState('');
  const [endReading, setEndReading] = useState('');
  const [minutes, setMinutes] = useState('');
  const [factor, setFactor] = useState(0.01);
  const [targetZone, setTargetZone] = useState(configs[0]?.zone_id || '');

  const gallons = startReading !== '' && endReading !== ''
    ? Math.max(0, (parseFloat(endReading) - parseFloat(startReading)) * factor)
    : null;
  const gpm = gallons !== null && parseFloat(minutes) > 0
    ? gallons / parseFloat(minutes)
    : null;

  return (
    <div className="rounded-[18px] border border-cyan-400/20 bg-cyan-950/30 mb-4 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-200/80">
          💧 GPM Calculator
        </span>
        <span className="text-slate-400 text-sm">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-slate-400 leading-5">
            Run a zone, record the meter before &amp; after, enter how many minutes it ran.
          </p>

          {/* Meter factor */}
          <div className="flex items-center gap-3">
            <label className="text-xs text-slate-400 w-28 shrink-0">Meter factor</label>
            <select
              value={factor}
              onChange={e => setFactor(parseFloat(e.target.value))}
              className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white focus:outline-none"
            >
              <option value={0.01} className="bg-[#0b1e30]">× 0.01 gal/unit (default)</option>
              <option value={0.1}  className="bg-[#0b1e30]">× 0.1 gal/unit</option>
              <option value={1}    className="bg-[#0b1e30]">× 1 gal/unit</option>
              <option value={7.48} className="bg-[#0b1e30]">× 7.48 (cubic feet → gal)</option>
              <option value={0.748} className="bg-[#0b1e30]">× 0.748 (0.1 cu ft → gal)</option>
            </select>
          </div>

          {/* Readings row */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Start reading</label>
              <input type="number" value={startReading} onChange={e => setStartReading(e.target.value)}
                placeholder="0000" className="w-full rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-sm text-white focus:outline-none text-right" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">End reading</label>
              <input type="number" value={endReading} onChange={e => setEndReading(e.target.value)}
                placeholder="0000" className="w-full rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-sm text-white focus:outline-none text-right" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Minutes ran</label>
              <input type="number" value={minutes} onChange={e => setMinutes(e.target.value)}
                placeholder="10" className="w-full rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-sm text-white focus:outline-none text-right" />
            </div>
          </div>

          {/* Result */}
          {gpm !== null && (
            <div className="rounded-[14px] border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs text-cyan-200/70 mb-0.5">Calculated GPM</p>
                <p className="text-2xl font-bold text-white tabular-nums">{gpm.toFixed(2)}</p>
                <p className="text-xs text-slate-400 mt-0.5">{gallons.toFixed(2)} gal over {minutes} min</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <select
                  value={targetZone}
                  onChange={e => setTargetZone(e.target.value)}
                  className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-white focus:outline-none max-w-[160px]"
                >
                  {configs.map(c => (
                    <option key={c.zone_id} value={c.zone_id} className="bg-[#0b1e30]">
                      Zone {c.zone_number} · {c.zone_name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => { onApply(targetZone, parseFloat(gpm.toFixed(2))); setOpen(false); }}
                  className="rounded-lg bg-cyan-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-cyan-400 transition"
                >
                  Apply to Zone ↓
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AdminZoneConfigEditor({ zones, deviceId, existingConfigs }) {
  const [configs, setConfigs] = useState(() => {
    const configMap = {};
    (existingConfigs || []).forEach(c => { configMap[c.zone_id] = c; });
    return zones.filter(z => z.enabled).map(z => ({
      zone_id: z.id,
      zone_number: z.number,
      zone_name: z.name,
      gpm: configMap[z.id]?.gpm || 0,
      area_sqft: configMap[z.id]?.area_sqft || 0,
    }));
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const applyCalculatedGpm = (zoneId, gpm) => {
    setConfigs(prev => prev.map(c => c.zone_id === zoneId ? { ...c, gpm } : c));
  };

  const updateField = (zoneId, field, value) => {
    setConfigs(prev => prev.map(c =>
      c.zone_id === zoneId ? { ...c, [field]: parseFloat(value) || 0 } : c
    ));
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const body = configs.map(c => ({
        zone_id: c.zone_id,
        zone_number: c.zone_number,
        gpm: c.gpm,
        area_sqft: c.area_sqft,
      }));
      const res = await fetch(`${BACKEND_URL}/api/rachio/zone-config/${deviceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': getAdminKey(),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to save');
      }
      setMessage({ type: 'success', text: 'Zone configs saved' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-[28px] border border-amber-400/20 bg-amber-950/20 p-6 shadow-2xl backdrop-blur-xl">
      <div className="flex items-center gap-2 mb-5">
        <div className="rounded-full bg-amber-400/20 p-1.5">
          <svg className="w-4 h-4 text-amber-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
          </svg>
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-200/80">Admin: Zone GPM Config</p>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 text-sm mb-4 ${message.type === 'success' ? 'bg-emerald-400/10 text-emerald-300' : 'bg-rose-400/10 text-rose-300'}`}>
          {message.text}
        </div>
      )}

      <GpmCalculator configs={configs} onApply={applyCalculatedGpm} />

      <div className="overflow-hidden rounded-[18px] border border-white/8 mb-4">
        <table className="w-full text-left text-sm">
          <thead className="bg-white/5 text-slate-400">
            <tr>
              <th className="px-4 py-3 font-medium">Zone</th>
              <th className="px-4 py-3 font-medium text-right">GPM</th>
              <th className="px-4 py-3 font-medium text-right">Area (sq ft)</th>
            </tr>
          </thead>
          <tbody>
            {configs.map(c => (
              <tr key={c.zone_id} className="border-t border-white/8 bg-slate-900/20">
                <td className="px-4 py-3 text-white font-medium">{c.zone_name}</td>
                <td className="px-4 py-3 text-right">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={c.gpm || ''}
                    onChange={e => updateField(c.zone_id, 'gpm', e.target.value)}
                    placeholder="0"
                    className="w-20 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-sm text-white text-right focus:border-cyan-400/50 focus:outline-none"
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={c.area_sqft || ''}
                    onChange={e => updateField(c.zone_id, 'area_sqft', e.target.value)}
                    placeholder="0"
                    className="w-24 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-sm text-white text-right focus:border-cyan-400/50 focus:outline-none"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="w-full rounded-xl bg-amber-500/80 py-3 text-sm font-bold text-white shadow-lg disabled:opacity-50 transition hover:bg-amber-500"
      >
        {saving ? 'Saving...' : 'Save Zone Configs'}
      </button>
    </div>
  );
}

// ── Admin Customer Switcher ─────────────────────────────────────────────────

function AdminCustomerSwitcher({ onSelect }) {
  const [rachioCustomers, setRachioCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [portalMsg, setPortalMsg] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/rachio/admin/customers`, {
      headers: { 'X-Admin-Key': getAdminKey() },
    })
      .then(r => r.json())
      .then(d => setRachioCustomers(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const searchCustomers = async () => {
    if (!search.trim()) return;
    setSearching(true);
    setResults([]);
    setPortalMsg(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/customers/search?q=${encodeURIComponent(search.trim())}`,
        { headers: { 'X-Admin-Key': getAdminKey() } }
      );
      const d = await res.json();
      setResults(Array.isArray(d) ? d : []);
    } catch {
      setPortalMsg({ type: 'error', text: 'Search failed' });
    } finally {
      setSearching(false);
    }
  };

  const openPortal = async (customerId) => {
    setPortalMsg({ type: 'info', text: 'Generating link...' });
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/portal-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': getAdminKey() },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      window.open(d.url + (d.url.includes('?') ? '&' : '?') + 'admin=1', '_blank');
      setPortalMsg({ type: 'success', text: `Opened portal for ${d.customer_name}` });
    } catch (e) {
      setPortalMsg({ type: 'error', text: e.message });
    }
  };

  return (
    <div className="rounded-[28px] border border-amber-400/20 bg-amber-950/20 p-6 shadow-2xl backdrop-blur-xl space-y-6">
      <div className="flex items-center gap-2">
        <div className="rounded-full bg-amber-400/20 p-1.5">
          <svg className="w-4 h-4 text-amber-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
          </svg>
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-200/80">Admin Tools</p>
      </div>

      {/* Open any customer's portal */}
      <div>
        <p className="text-sm font-semibold text-white mb-3">Open Customer Portal</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchCustomers()}
            placeholder="Customer name (e.g. Smith)"
            className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-400/50 focus:outline-none"
          />
          <button
            onClick={searchCustomers}
            disabled={searching}
            className="rounded-xl bg-amber-500/20 border border-amber-400/30 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-500/30 transition disabled:opacity-50"
          >
            {searching ? '...' : 'Search'}
          </button>
        </div>

        {portalMsg && (
          <p className={`mt-2 text-xs font-semibold ${portalMsg.type === 'success' ? 'text-emerald-300' : portalMsg.type === 'error' ? 'text-rose-300' : 'text-slate-300'}`}>
            {portalMsg.text}
          </p>
        )}

        {results.length > 0 && (
          <div className="mt-3 space-y-1.5 max-h-60 overflow-y-auto">
            {results.map(c => (
              <div key={c.name} className="flex items-center justify-between rounded-[14px] border border-white/8 bg-white/5 px-4 py-2.5">
                <div>
                  <p className="text-sm font-semibold text-white">{c.customer_name}</p>
                  {c.email_id && <p className="text-xs text-slate-400">{c.email_id}</p>}
                </div>
                <button
                  onClick={() => openPortal(c.name)}
                  className="rounded-lg bg-cyan-500/20 border border-cyan-400/30 px-3 py-1.5 text-xs font-bold text-cyan-200 hover:bg-cyan-500/30 transition"
                >
                  Open Portal ↗
                </button>
              </div>
            ))}
          </div>
        )}

        {results.length === 0 && !searching && search && (
          <p className="mt-2 text-xs text-slate-400">No results. Try a shorter name or check ERPNext spelling.</p>
        )}
      </div>

      {/* Rachio customer switcher (for dashboard view) */}
      {rachioCustomers.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-white mb-2">Switch Rachio Dashboard</p>
          <select
            onChange={e => onSelect(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-cyan-400/50 focus:outline-none"
            defaultValue=""
          >
            <option value="" className="bg-[#0b1e30]">Select a customer...</option>
            {rachioCustomers.map(c => (
              <option key={c.customer_id} value={c.customer_id} className="bg-[#0b1e30]">
                {c.customer_id} — {c.device_name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ── Watering History Section ────────────────────────────────────────────────

function WateringHistory({ customerId }) {
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const now = Date.now();
    const weekAgo = now - 7 * 86400000;
    fetch(`${BACKEND_URL}/api/rachio/history/${customerId}?start=${weekAgo}&end=${now}`)
      .then(r => r.json())
      .then(d => setRuns(d.runs || []))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (loading) {
    return (
      <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-5">Recent Watering</p>
        <div className="flex items-center gap-3 text-sm text-slate-400 py-4 justify-center">
          <div className="w-4 h-4 border border-slate-500 border-t-cyan-400 rounded-full animate-spin" />
          Loading history...
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70 mb-5">Recent Watering (Last 7 Days)</p>
      {(!runs || runs.length === 0) ? (
        <p className="text-sm text-slate-400 text-center py-4">No watering events in the last 7 days.</p>
      ) : (
        <div className="space-y-4">
          {runs.slice(0, 10).map((run, ri) => (
            <details key={ri} className="rounded-[20px] border border-white/8 bg-white/5 overflow-hidden group">
              <summary className="px-4 py-3 cursor-pointer flex items-center justify-between hover:bg-white/5 transition list-none">
                <div className="flex items-center gap-3">
                  <svg className="w-4 h-4 text-cyan-300 group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-white">{run.schedule_name}</p>
                    <p className="text-xs text-slate-400">{run.started_at ? new Date(run.started_at).toLocaleString() : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-300">
                  <span>{run.total_minutes} min</span>
                  <span>{run.total_gallons != null ? `${run.total_gallons} gal` : '--'}</span>
                </div>
              </summary>
              <div className="px-4 pb-3 border-t border-white/5">
                <table className="w-full text-xs mt-2">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left py-1 font-medium">Zone</th>
                      <th className="text-right py-1 font-medium">Duration</th>
                      <th className="text-right py-1 font-medium">Gallons</th>
                      <th className="text-right py-1 font-medium">Inches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.zones.map((z, zi) => (
                      <tr key={zi} className="text-slate-300">
                        <td className="py-1 text-white">{z.zone_name}</td>
                        <td className="py-1 text-right">{z.duration_min} min</td>
                        <td className="py-1 text-right">{z.gallons != null ? z.gallons : '--'}</td>
                        <td className="py-1 text-right">{z.inches != null ? `${z.inches}"` : '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Overview Zone Cell with Photo ────────────────────────────────────────────

function OverviewZoneCell({ zone, customerId, onOpenDetail }) {
  const { photo } = useZonePhoto(zone);

  return (
    <div className="flex items-center gap-3">
      <button onClick={onOpenDetail} className="shrink-0 focus:outline-none">
        {photo
          ? <img src={photo} alt={zone.name} className="w-12 h-12 rounded-xl object-cover border border-white/10 hover:opacity-80 transition" onError={() => {}} />
          : <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-xl hover:bg-white/10 transition">🌿</div>
        }
      </button>
      <button onClick={onOpenDetail} className="font-medium text-white hover:text-cyan-300 transition text-left">
        {zone.name}
      </button>
    </div>
  );
}


export default function AAAIrrigationDashboard() {
  const navigate = useNavigate();
  const { needsPicker, selected, locations, setSelected, propertyReady, status: propertyStatus, errorCode: propertyError, retry: retryProperties } = useServiceLocation();
  const [rachio, setRachio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skippedRachio, setSkippedRachio] = useState(() => sessionStorage.getItem('rachio_skipped') === '1');
  const [showSchedule, setShowSchedule] = useState(false);
  const [visitsVersion, setVisitsVersion] = useState(0); // re-reads Upcoming Visits after an inspection change
  const [activePanel, setActivePanel] = useState(null); // 'invoices' | 'estimates' | 'letters' | 'actions' | null
  const [hasLetters, setHasLetters] = useState(false);
  const [autoEstimate, setAutoEstimate] = useState(null);
  const [autoInvoice, setAutoInvoice] = useState(null);
  const [rachioTab, setRachioTab] = useState('overview'); // 'overview' | 'controls' | 'usage' | 'history'
  const [detailZone, setDetailZone] = useState(null); // zone opened via photo click
  const [wateringSchedule, setWateringSchedule] = useState(null);
  // Real measured GPM per zone (backend/watersmart_zone_metrics.py), used to
  // show the zone table's Flow column in gallons — Rachio's own zone.flow
  // ("nozzle inchesPerHour") is an application-rate unit, not gallons, and
  // this dashboard is gallon-based everywhere else. Same pattern as
  // WaterConservation.js's OverviewTab/ControlsTab zoneMetricsFor.
  const [zoneMetrics, setZoneMetrics] = useState(null);

  const customer = JSON.parse(localStorage.getItem('customer') || '{}');
  const isAdmin = new URLSearchParams(window.location.search).get('admin') === '1';
  const [adminCustomerId, setAdminCustomerId] = useState(null);
  const activeCustomerId = adminCustomerId || customer.id;
  // Irrigation Zone Schedule nav: shown ONLY when the backend/ERP confirm the
  // feature is enabled for the selected property (or the customer's sole
  // property). Any error or disabled answer keeps the button hidden.
  const [zoneScheduleEnabled, setZoneScheduleEnabled] = useState(false);
  useEffect(() => {
    setZoneScheduleEnabled(false);
    if (isAdmin || !customer.id || !propertyReady) return undefined;
    let cancelled = false;
    const qs = selected ? `?service_location=${encodeURIComponent(selected)}` : '';
    fetch(`${BACKEND_URL}/api/customer/${encodeURIComponent(customer.id)}/zone-schedule${qs}`)
      .then(r => (r.ok ? r.json() : { enabled: false }))
      .then(d => { if (!cancelled) setZoneScheduleEnabled(!!d.enabled); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [customer.id, isAdmin, selected, propertyReady]);

  const fetchRachio = useCallback(async (custId) => {
    const cid = custId || activeCustomerId;
    if (!cid) { setLoading(false); return; }
    try {
      const params = new URLSearchParams();
      if (isAdmin) params.set('admin', '1');
      // `selected` is scoped to the logged-in customer's own service
      // locations (ServiceLocationProvider is keyed off localStorage
      // customer.id) -- in admin mode, cid/activeCustomerId can be a
      // DIFFERENT customer entirely, so never forward it there.
      if (!isAdmin && selected) params.set('service_location', selected);
      const qs = params.toString();
      const url = `${BACKEND_URL}/api/rachio/dashboard/${cid}${qs ? `?${qs}` : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      setRachio(data);
    } catch {
      setRachio({ supported: false, reason: 'error' });
    } finally {
      setLoading(false);
    }
  }, [activeCustomerId, isAdmin, selected]);

  // Watering Schedule card: shown for every active property (2026-10-18), with or
  // without a zone schedule or controller; ERP decides the days ("Watering days not
  // verified" when unknown). Follows the selected property for multi-property customers.
  useEffect(() => {
    if (!customer.id || !propertyReady) return undefined;
    let cancelled = false;
    setWateringSchedule(null);
    const qs = selected ? `?service_location=${encodeURIComponent(selected)}` : '';
    fetch(`${BACKEND_URL}/api/customer/${encodeURIComponent(customer.id)}/watering-schedule${qs}`)
      .then((r) => (r.ok ? r.json() : { enabled: false, schedule: null }))
      .then((data) => { if (!cancelled) setWateringSchedule(data && data.enabled ? data.schedule : null); })
      .catch(() => { if (!cancelled) setWateringSchedule(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id, selected, propertyReady]);

  // WaterSmart per-zone gallons for the selected property's controller. In
  // admin mode the viewed customer can differ from the logged-in one, so the
  // logged-in customer's property is never forwarded there.
  useEffect(() => {
    if (!activeCustomerId) return undefined;
    if (!isAdmin && !propertyReady) return undefined;
    let cancelled = false;
    setZoneMetrics(null);
    const qs = !isAdmin && selected ? `?service_location=${encodeURIComponent(selected)}` : '';
    fetch(`${BACKEND_URL}/api/watersmart/zone-metrics/${encodeURIComponent(activeCustomerId)}${qs}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setZoneMetrics(d); })
      .catch(() => { if (!cancelled) setZoneMetrics({ available: false, rows: [], anomalies: [] }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCustomerId, isAdmin, selected, propertyReady]);

  // Letters tab is staff-authored and most customers never have one — only
  // show the button/panel when at least one Letter exists on file.
  useEffect(() => {
    if (!customer?.id) return;
    fetch(`${BACKEND_URL}/api/customer/${customer.id}/letters`)
      .then((r) => r.json())
      .then((d) => setHasLetters(Array.isArray(d.data) && d.data.length > 0))
      .catch(() => setHasLetters(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  useEffect(() => {
    const openEstimate = localStorage.getItem('open_estimate');
    if (openEstimate) {
      localStorage.removeItem('open_estimate');
      setAutoEstimate(openEstimate);
    }
    const openInvoice = localStorage.getItem('open_invoice');
    if (openInvoice) {
      localStorage.removeItem('open_invoice');
      setAutoInvoice(openInvoice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when admin switches customer
  useEffect(() => {
    if (adminCustomerId) {
      setLoading(true);
      fetchRachio(adminCustomerId);
    }
  }, [adminCustomerId, fetchRachio]);

  // Load (and re-load on every property switch) the Rachio controller -- only
  // once the property is known, so a multi-property customer never sees a
  // guessed/default property's controller (2026-10-15).
  useEffect(() => {
    if (adminCustomerId) return;
    if (!isAdmin && !propertyReady) return;
    setLoading(true);
    fetchRachio();
  }, [propertyReady, selected, adminCustomerId, isAdmin, fetchRachio]);

  if (!isAdmin && propertyStatus === 'error') {
    const signInAgain = propertyError === 'forbidden' || propertyError === 'no_session';
    return (
      <div className="error-page" data-testid="property-load-error">
        <div className="error-card">
          <h2>{signInAgain ? 'Please sign in again' : "We couldn't load your properties"}</h2>
          <p>
            {signInAgain
              ? 'Your sign-in does not match this account. Sign in again to continue.'
              : 'Something went wrong loading your account. Please try again.'}
          </p>
          {signInAgain ? (
            <button className="btn-primary" onClick={() => { clearPortalSession(); navigate('/', { replace: true }); }}>
              Sign in
            </button>
          ) : (
            <button className="btn-primary" onClick={retryProperties}>Try again</button>
          )}
        </div>
      </div>
    );
  }

  if (needsPicker) {
    return <PropertyPicker />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#1186c5_0%,_#0b3b57_35%,_#082c40_100%)] flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-10 h-10 border-2 border-cyan-300/30 border-t-cyan-300 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-300">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Full-screen "not linked" onboarding only for single-property customers. With
  // several properties it would hide the property switcher (and the other
  // properties' controllers), so they get the dashboard with an inline notice.
  if (!rachio?.supported && !skippedRachio && !isAdmin && locations.length <= 1) {
    return <ControllerNotLinked wateringSchedule={wateringSchedule} onSkip={() => {
      sessionStorage.setItem('rachio_skipped', '1');
      setSkippedRachio(true);
    }} />;
  }

  const hasRachio = rachio?.supported;
  const { controller, zones, healthy_count, zone_count, schedules } = hasRachio
    ? rachio
    : { controller: null, zones: [], healthy_count: 0, zone_count: 0, schedules: [] };


  const stats = hasRachio ? [
    {
      label: 'Controller',
      value: controller.status === 'ONLINE' ? 'Online' : 'Offline',
      sub: controller.name,
      alert: controller.status !== 'ONLINE',
    },
    {
      label: 'Zones',
      value: `${healthy_count} / ${zone_count}`,
      sub: `${healthy_count} healthy`,
    },
    {
      label: 'Schedule Mode',
      value: controller.scheduleMode === 'MANUAL' ? 'Manual' : 'Smart',
      sub: `${schedules.filter(s => s.enabled).length} active schedules`,
    },
    {
      label: 'Watering Plan',
      value: controller.on ? 'Running' : 'Standby',
      sub: 'Optimized for North Texas turf',
    },
  ] : [];

  const UpcomingVisitsList = ({ customerId }) => {
    const [visits, setVisits] = useState(null);
    useEffect(() => {
      const url = `${BACKEND_URL}/api/customer/${customerId}/quotations` +
        (selected ? `?service_location=${encodeURIComponent(selected)}` : '');
      fetch(url)
        .then(r => r.json())
        .then(data => {
          const today = new Date().toISOString().split('T')[0];
          // A cancelled appointment (pipeline status "Canceled") is not an upcoming
          // visit; customers can now cancel inspections from the portal.
          const upcoming = (data.data || []).filter(q =>
            q.custom_scheduled_date && q.custom_scheduled_date >= today &&
            q.docstatus !== 2 && !['Canceled', 'Cancelled'].includes(q.custom_pipeline_status)
          );
          setVisits(upcoming);
        })
        .catch(() => setVisits([]));
    }, [customerId, selected]);

    if (!visits) return <p className="text-sm text-slate-400">Loading appointments...</p>;
    if (visits.length === 0) return <p className="text-sm text-slate-400">No upcoming visits scheduled.</p>;

    return visits.map(q => {
      const d = new Date(q.custom_scheduled_date + 'T12:00:00');
      const dateStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      return (
        <div key={q.name} className="rounded-[22px] border border-white/10 bg-white/5 p-4">
          <p className="text-base font-semibold text-white">{q.custom_service_description || 'Service Visit'}</p>
          <p className="mt-1 text-sm text-cyan-200">{dateStr}{q.custom_scheduled_time ? ` · ${q.custom_scheduled_time}` : ''}</p>
          {q.custom_service_address && <p className="mt-1 text-xs text-slate-400">{q.custom_service_address}</p>}
        </div>
      );
    });
  };

  // The property inspections are for: the picked one, or a customer's only
  // Service Location (the picker never auto-selects a single one).
  const inspectionLocation = selected || (locations.length === 1 ? locations[0].name : null);

  const alerts = hasRachio
    ? (controller.status !== 'ONLINE'
        ? [`Your Rachio controller (${controller.name}) appears to be offline. Check your WiFi connection or the device itself.`]
        : zones.filter(z => z.status === 'Alert').map(z => `${z.name} may need attention -- check for clogged nozzles or pressure issues.`))
    : [];

  const togglePanel = (panel) => setActivePanel(p => p === panel ? null : panel);

  const rachioTabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'controls', label: 'Controls' },
    { key: 'usage', label: 'Usage' },
    { key: 'history', label: 'History' },
  ];

  const pageShell = (children) => (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#1186c5_0%,_#0b3b57_35%,_#082c40_100%)] text-white">
      {showSchedule && <ScheduleModal customer={customer} onClose={() => setShowSchedule(false)} />}
      {autoEstimate && (() => {
        window.open(`/estimates?id=${autoEstimate}&customer=${encodeURIComponent(customer.id)}`, '_blank');
        setAutoEstimate(null);
        return null;
      })()}
      {autoInvoice && (() => {
        window.open(`/invoice?id=${autoInvoice}&customer=${encodeURIComponent(customer.id)}`, '_blank');
        setAutoInvoice(null);
        return null;
      })()}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-[-8%] top-[12%] h-[520px] w-[520px] rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute right-[-10%] top-[8%] h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 py-8 lg:px-10">
        {/* Header */}
        <header className="mb-8 flex flex-col gap-6 rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-cyan-300/20 bg-white/5 shadow-lg">
              <img src="/logo.png" alt="AAA Irrigation Service logo" className="h-14 w-14 rounded-full object-cover" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">AAA Irrigation Service</p>
              <h1 className="mt-1 text-3xl font-semibold tracking-tight">Customer Dashboard</h1>
              {customer.name && <p className="mt-1 text-sm text-slate-300">Welcome back, {customer.name}</p>}
              {isAdmin && (
                <p className="mt-0.5 text-xs font-semibold text-amber-300 uppercase tracking-wider">Admin Mode</p>
              )}
              {locations.length > 1 && (
                <div className="mt-2">
                  <label className="mr-2 text-xs font-semibold uppercase tracking-wider text-cyan-200/70">
                    Property
                  </label>
                  <select
                    data-testid="property-switcher"
                    value={selected || ''}
                    onChange={(e) => setSelected(e.target.value)}
                    className="rounded-lg border border-white/10 bg-slate-900/60 px-2 py-1 text-sm text-white"
                  >
                    {locations.map(loc => (
                      <option key={loc.name} value={loc.name}>
                        {loc.location_name || loc.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className={`grid gap-3 sm:grid-cols-3 ${hasLetters ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}>
            <button
              onClick={() => setShowSchedule(true)}
              className="rounded-2xl border border-cyan-300/20 bg-cyan-400/10 px-4 py-3 text-sm font-medium text-cyan-100 transition hover:bg-cyan-400/20"
            >
              Schedule Service
            </button>
            <button
              onClick={() => togglePanel('estimates')}
              className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${activePanel === 'estimates' ? 'border-cyan-400/40 bg-cyan-400/20 text-cyan-100' : 'border-white/10 bg-white/5 text-slate-100 hover:bg-white/10'}`}
            >
              {activePanel === 'estimates' ? 'Hide Estimates' : 'View Estimates'}
            </button>
            <button
              onClick={() => togglePanel('invoices')}
              className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${activePanel === 'invoices' ? 'border-cyan-400/40 bg-cyan-400/20 text-cyan-100' : 'border-white/10 bg-white/5 text-slate-100 hover:bg-white/10'}`}
            >
              {activePanel === 'invoices' ? 'Hide Invoices' : 'View Invoices'}
            </button>
            <button
              onClick={() => togglePanel('actions')}
              className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${activePanel === 'actions' ? 'border-cyan-400/40 bg-cyan-400/20 text-cyan-100' : 'border-white/10 bg-white/5 text-slate-100 hover:bg-white/10'}`}
            >
              {activePanel === 'actions' ? 'Hide Files' : 'View Files'}
            </button>
            {hasLetters && (
              <button
                onClick={() => togglePanel('letters')}
                className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${activePanel === 'letters' ? 'border-cyan-400/40 bg-cyan-400/20 text-cyan-100' : 'border-white/10 bg-white/5 text-slate-100 hover:bg-white/10'}`}
              >
                {activePanel === 'letters' ? 'Hide Letters' : 'View Letters'}
              </button>
            )}
            {zoneScheduleEnabled && (
              <button
                data-testid="zone-schedule-nav"
                onClick={() => navigate(selected ? `/zone-schedule?service_location=${encodeURIComponent(selected)}` : '/zone-schedule')}
                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-slate-100 transition hover:bg-white/10"
              >
                Zone Schedule
              </button>
            )}
            <button
              onClick={() => navigate('/water-conservation')}
              className="rounded-2xl border border-cyan-300/20 bg-cyan-400/10 px-4 py-3 text-sm font-medium text-cyan-100 transition hover:bg-cyan-400/20"
            >
              Water Conservation
            </button>
          </div>
        </header>

        {/* Invoices / Estimates / Photos panels */}
        {activePanel === 'estimates' && (
          <div className="mb-8">
            <EstimatesSection customerId={customer.id} />
          </div>
        )}
        {activePanel === 'invoices' && (
          <div className="mb-8">
            <InvoicesSection customerId={customer.id} />
          </div>
        )}
        {hasLetters && activePanel === 'letters' && (
          <div className="mb-8">
            <LettersSection customerId={customer.id} />
          </div>
        )}
        {activePanel === 'actions' && (
          <div className="mb-8 space-y-6">
            <ImagesGallery customerId={customer.customer_id || customer.id} />
            <Documents customerId={customer.customer_id || customer.id} />
          </div>
        )}


        {/* Admin customer switcher */}
        {isAdmin && (
          <div className="mb-8">
            <AdminCustomerSwitcher onSelect={(cid) => {
              if (cid) {
                setAdminCustomerId(cid);
              }
            }} />
          </div>
        )}

        {children}
      </div>
    </div>
  );

  if (!hasRachio) {
    return pageShell(
      <>
      {/* The property's watering days/city rule stay visible without a controller. */}
      {wateringSchedule && <WateringScheduleCard schedule={wateringSchedule} />}
      <section className="mt-6 grid gap-6 xl:grid-cols-[1.35fr_0.85fr]" data-testid="no-controller-section">
        <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-8 shadow-2xl backdrop-blur-xl flex flex-col items-center justify-center text-center gap-4">
          <div className="rounded-full border border-cyan-300/20 bg-cyan-400/10 p-5">
            <svg className="w-10 h-10 text-cyan-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white">No Smart Controller Connected</h2>
          <p className="text-sm text-slate-300 max-w-sm leading-6">
            {locations.length > 1
              ? "This property doesn't have a Rachio smart controller linked yet. Switch properties above, or call us to get one set up here."
              : "Your account doesn't have a Rachio smart controller linked yet. We install and configure Rachio systems -- call us to get set up."}
          </p>
          <a href="tel:4697513567" className="mt-2 rounded-xl bg-[linear-gradient(90deg,#1094ff,#19d3b3)] px-6 py-3 text-sm font-bold tracking-wide text-white shadow-lg">
            Call Us -- 469-751-3567
          </a>
        </div>

        <div className="space-y-6">
          <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">Upcoming Visits</p>
            <div className="mt-5 space-y-4">
              <UpcomingVisitsList key={visitsVersion} customerId={customer.id} />
              <InspectionsCard customerId={customer.id} serviceLocation={inspectionLocation} onChanged={() => setVisitsVersion(v => v + 1)} />
            </div>
          </div>
          <div className="rounded-[28px] border border-white/10 bg-[linear-gradient(135deg,rgba(16,148,255,0.18),rgba(25,211,179,0.14))] p-6 shadow-2xl backdrop-blur-xl">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-50/75">Powered by</p>
            <h3 className="mt-3 text-2xl font-semibold text-white">Boatman Systems</h3>
            <p className="mt-3 text-sm leading-6 text-slate-100/80">Secure customer access, service coordination, and irrigation intelligence built into one branded system.</p>
          </div>
        </div>
      </section>
      </>
    );
  }

  return pageShell(
    <>
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <div key={item.label} className={`rounded-[24px] border p-5 shadow-xl backdrop-blur-xl ${item.alert ? 'border-rose-400/30 bg-rose-950/40' : 'border-white/10 bg-slate-950/45'}`}>
            <p className="text-sm font-medium text-slate-300">{item.label}</p>
            <p className={`mt-3 text-3xl font-semibold ${item.alert ? 'text-rose-300' : 'text-white'}`}>{item.value}</p>
            <p className="mt-2 text-sm text-cyan-100/75">{item.sub}</p>
          </div>
        ))}
      </section>

      {wateringSchedule && <WateringScheduleCard schedule={wateringSchedule} />}

      {/* Rachio tab navigation */}
      {hasRachio && (
        <div className="mt-6 flex rounded-xl border border-white/10 overflow-hidden w-fit">
          {rachioTabs.map(t => (
            <button
              key={t.key}
              onClick={() => setRachioTab(t.key)}
              className={`px-5 py-2.5 text-sm font-semibold transition ${rachioTab === t.key ? 'bg-cyan-400/20 text-cyan-100' : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-slate-200'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab content */}
      {rachioTab === 'overview' && (
        <section className="mt-6 grid gap-6 xl:grid-cols-[1.35fr_0.85fr]">
          <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">System Overview</p>
                <h2 className="mt-2 text-2xl font-semibold">Irrigation Zones</h2>
              </div>
              <div className="rounded-full border border-emerald-300/20 bg-emerald-400/10 px-3 py-1 text-sm font-medium text-emerald-200">
                {healthy_count} Healthy · {zone_count - healthy_count} Needs Review
              </div>
            </div>
            <div className="overflow-hidden rounded-[22px] border border-white/8">
              <table className="w-full text-left">
                <thead className="bg-white/5 text-sm text-slate-300">
                  <tr>
                    <th className="px-5 py-4 font-medium">Zone</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 font-medium">Runtime</th>
                    <th className="px-5 py-4 font-medium">Flow</th>
                  </tr>
                </thead>
                <tbody>
                  {(isAdmin ? zones : zones.filter(z => z.enabled)).map((zone) => (
                    <tr key={zone.name} className="border-t border-white/8 bg-slate-900/20">
                      <td className="px-5 py-4">
                        <OverviewZoneCell zone={zone} customerId={activeCustomerId} onOpenDetail={() => setDetailZone(zone)} />
                      </td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          zone.status === 'Healthy'  ? 'bg-emerald-400/15 text-emerald-200' :
                          zone.status === 'Watch'    ? 'bg-amber-400/15 text-amber-200' :
                          zone.status === 'Disabled' ? 'bg-slate-400/15 text-slate-400' :
                                                       'bg-rose-400/15 text-rose-200'
                        }`}>{zone.status}</span>
                      </td>
                      <td className="px-5 py-4 text-slate-200">{zone.runtime}</td>
                      <td className="px-5 py-4 text-slate-200">{zoneFlowGallonsLabel(zoneMetrics, zone)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-[22px] border border-cyan-300/10 bg-cyan-400/5 p-5">
                <p className="text-sm font-semibold text-cyan-100">Controller Info</p>
                <div className="mt-3 space-y-2 text-sm text-slate-200">
                  <div className="flex justify-between"><span>Model</span><span>{controller.model?.replace(/_/g,' ')}</span></div>
                  <div className="flex justify-between"><span>Mode</span><span>{controller.scheduleMode}</span></div>
                </div>
              </div>
              <div className="rounded-[22px] border border-white/10 bg-white/5 p-5">
                <p className="text-sm font-semibold text-white">Active Schedules</p>
                <div className="mt-3 space-y-2 text-sm text-slate-200">
                  {schedules.length === 0
                    ? <p className="text-slate-400">No schedules found</p>
                    : schedules.slice(0, 3).map(s => (
                        <div key={s.id} className="flex justify-between">
                          <span>{s.name || 'Schedule'}</span>
                          <span className={s.enabled ? 'text-emerald-300' : 'text-slate-500'}>{s.enabled ? 'Active' : 'Paused'}</span>
                        </div>
                      ))
                  }
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <WeatherWidget />
            <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">Upcoming Visits</p>
              <div className="mt-5 space-y-4">
                <div className="rounded-[22px] border border-white/10 bg-white/5 p-4">
                  <p className="text-base font-semibold text-white">Spring Startup Visit</p>
                  <p className="mt-3 text-sm leading-6 text-slate-300">Technician will test controller, valves, and heads.</p>
                </div>
                <InspectionsCard customerId={customer.id} serviceLocation={inspectionLocation} onChanged={() => setVisitsVersion(v => v + 1)} />
              </div>
            </div>
            {alerts.length > 0 && (
              <div className="rounded-[28px] border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur-xl">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200/70">Alerts & Notes</p>
                <div className="mt-5 space-y-3">
                  {alerts.map((alert) => (
                    <div key={alert} className="rounded-[20px] border border-amber-300/15 bg-amber-400/10 p-4 text-sm leading-6 text-amber-50">{alert}</div>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-[28px] border border-white/10 bg-[linear-gradient(135deg,rgba(16,148,255,0.18),rgba(25,211,179,0.14))] p-6 shadow-2xl backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-50/75">Powered by</p>
              <h3 className="mt-3 text-2xl font-semibold text-white">Boatman Systems</h3>
              <p className="mt-3 text-sm leading-6 text-slate-100/80">Secure customer access, service coordination, and irrigation intelligence built into one branded system.</p>
            </div>
            <WaterSmartCard customerId={activeCustomerId} />
          </div>
        </section>
      )}

      {/* Zone detail modal — accessible from any tab */}
      {detailZone && (
        <ZoneDetailModal
          zone={detailZone}
          customerId={activeCustomerId}
          onClose={() => setDetailZone(null)}
          zoneMetrics={zoneMetrics}
          selected={isAdmin ? null : selected}
        />
      )}

      {rachioTab === 'controls' && (
        <section className="mt-6 grid gap-6 xl:grid-cols-[1.35fr_0.85fr]">
          <ZoneControls
            zones={zones}
            customerId={activeCustomerId}
            onRefresh={() => fetchRachio()}
            onOpenDetail={setDetailZone}
            zoneMetrics={zoneMetrics}
            selected={isAdmin ? null : selected}
          />
          <div className="space-y-6">
            <ScheduleToggles
              schedules={schedules}
              customerId={activeCustomerId}
              zones={zones}
              onRefresh={() => fetchRachio()}
              isAdmin={isAdmin}
            />
          </div>
        </section>
      )}

      {rachioTab === 'usage' && (
        <section className="mt-6">
          <WaterUsageDashboard customerId={activeCustomerId} />
        </section>
      )}

      {rachioTab === 'history' && (
        <section className="mt-6">
          <WateringHistory customerId={activeCustomerId} />
        </section>
      )}

      {/* Admin zone config editor + zone toggles */}
      {isAdmin && hasRachio && (
        <section className="mt-6 grid gap-6 xl:grid-cols-2">
          <AdminZoneConfigEditor
            zones={zones}
            deviceId={controller.id}
            existingConfigs={rachio.zone_configs || []}
          />
          <AdminZoneToggles
            zones={zones}
            onRefresh={() => fetchRachio()}
          />
        </section>
      )}
    </>
  );
}
